import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { toast } from "sonner";
import "@/App.css";
import { PORTFOLIO, MARKET_SCAN_TICKERS, QUICK_QUESTIONS, TOP_INVESTORS } from "./data/portfolio";
import { finnhubAPI, aiAPI, algoAPI, portfolioAPI, marketAPI, authAPI, newsAPI, briefingAPI, watchlistAPI, investorsAPI, earningsAPI, trackRecordAPI, trading212API, massiveScanAPI, priceAlertsAPI, topPicksAPI, capitalFlowAPI, institutionalAPI, gemsAPI, convictionAPI } from "./services/api";
import PerformanceTab from "./components/PerformanceTab";
import { StockSearch } from "./components/StockSearch";
import MomentumWatchlist from "./components/MomentumWatchlist";
import AnalystTab from "./components/AnalystTab";
import CapitalFlowPanel from "./components/CapitalFlowPanel";
import InsiderSmartMoney from "./components/InsiderSmartMoney";
import USGovernmentHoldings from "./components/USGovernmentHoldings";
import PoliticiansWatch from "./components/PoliticiansWatch";
import LongTermIdeas from "./components/LongTermIdeas";
import PreEarningsRisk from "./components/PreEarningsRisk";
import SmartAnalyzer from "./components/SmartAnalyzer";
import { SmartPortfolioAnalyzer, SmartFinderAnalyzer } from "./components/SmartPortfolioAnalyzer";
import SmartScreener from "./components/SmartScreener";
import BriefingExecutor from "./components/BriefingExecutor";
import PortfolioEngine from "./components/PortfolioEngine";
import IntradayPullback from "./components/IntradayPullback";
import EarningsRadar from "./components/EarningsRadar";
import DisciplinedBot from "./components/DisciplinedBot";
import EarlyAlpha from "./components/EarlyAlpha";
import InnovationLab from "./components/InnovationLab";
import SniperBot from "./components/SniperBot";
import PortfolioTab from "./components/PortfolioTab";
import CopyTradeTab from "./components/CopyTradeTab";
import WatchlistTab from "./components/WatchlistTab";
import ScannerTab from "./components/ScannerTab";
import BotTab from "./components/BotTab";
import GemsTab from "./components/GemsTab";
import AlertsPanel from "./components/AlertsPanel";
import PullbackOpportunities from "./components/PullbackOpportunities";
import AlgoTab from "./components/AlgoTab";
import { 
  parseCandles, 
  calculateSupportResistance, 
  getSignal, 
  formatNumber, 
  formatPercent,
  formatTickerPrice,
  getTickerCurrency,
  getTickerCurrencyCode,
  delay 
} from "./utils/trading";
import { 
  Activity, 
  TrendingUp, 
  TrendingDown, 
  Zap, 
  Search, 
  Newspaper, 
  Bot,
  RefreshCw,
  Send,
  ChevronRight,
  AlertTriangle,
  Target,
  Wallet,
  BarChart3,
  Clock,
  ArrowUpRight,
  ArrowDownRight,
  Cpu,
  Shield,
  Fuel,
  Bitcoin,
  Rocket,
  Building2,
  Atom,
  LineChart,
  Radar,
  Calculator,
  History,
  PieChart,
  Gauge,
  Play,
  ArrowRight,
  Check,
  X,
  Minus,
  Plus,
  Edit3,
  Trash2,
  Save,
  CircleDot,
  LogOut,
  User,
  Key,
  Mail,
  Lock,
  Eye,
  EyeOff,
  Settings,
  FileText,
  MessageSquare,
  Star,
  Gem,
  Coins,
  Droplets,
  Globe,
  Layers,
  Scale,
  Download,
  CalendarDays,
  Mic,
  MicOff,
  ChevronDown,
  ChevronUp,
  ShieldAlert,
  Crosshair,
  ImagePlus,
  Bell,
  BellRing,
  ShieldCheck,
  Sparkles,
  FlaskConical,
} from "lucide-react";
import {
  AreaChart, Area, XAxis, YAxis, Tooltip as RechartsTooltip, ResponsiveContainer, ReferenceLine, Bar, ComposedChart, Cell
} from "recharts";

// Format currency in EUR
const formatEUR = (num) => {
  if (num === null || num === undefined) return "—";
  return `${num.toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}€`;
};

// Sparkline component
function Sparkline({ values, width = 80, height = 28 }) {
  if (!values || values.length < 2) {
    return <span className="text-slate-500 text-xs">—</span>;
  }
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const points = values
    .map((v, i) => `${(i / (values.length - 1)) * width},${height - ((v - min) / range) * height}`)
    .join(" ");
  const isUp = values[values.length - 1] >= values[0];
  return (
    <svg width={width} height={height} className="inline-block">
      <polyline points={points} fill="none" stroke={isUp ? "#22c55e" : "#ef4444"} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round"/>
    </svg>
  );
}

// Score Gauge component
function ScoreGauge({ score, size = 80 }) {
  const radius = (size - 10) / 2;
  const circumference = radius * Math.PI;
  const progress = (score / 100) * circumference;
  const color = score >= 70 ? "#22c55e" : score >= 40 ? "#eab308" : "#ef4444";
  
  return (
    <div className="relative inline-flex items-center justify-center" style={{ width: size, height: size / 2 + 10 }}>
      <svg width={size} height={size / 2 + 10} className="transform -rotate-0">
        <path
          d={`M 5 ${size / 2} A ${radius} ${radius} 0 0 1 ${size - 5} ${size / 2}`}
          fill="none"
          stroke="#27272a"
          strokeWidth="8"
          strokeLinecap="round"
        />
        <path
          d={`M 5 ${size / 2} A ${radius} ${radius} 0 0 1 ${size - 5} ${size / 2}`}
          fill="none"
          stroke={color}
          strokeWidth="8"
          strokeLinecap="round"
          strokeDasharray={`${progress} ${circumference}`}
          style={{ filter: `drop-shadow(0 0 6px ${color})` }}
        />
      </svg>
      <div className="absolute bottom-0 text-center">
        <span className="font-mono text-2xl font-bold" style={{ color }}>{score}</span>
      </div>
    </div>
  );
}

// Price tick with flash animation
function PriceTick({ price, prevPrice, ticker }) {
  const [flashClass, setFlashClass] = useState("");
  useEffect(() => {
    if (!prevPrice || !price || price === prevPrice) return;
    setFlashClass(price > prevPrice ? "flash-up" : "flash-down");
    const timer = setTimeout(() => setFlashClass(""), 600);
    return () => clearTimeout(timer);
  }, [price, prevPrice]);
  const colorClass = !price ? "text-slate-500" : flashClass === "flash-up" ? "text-emerald-400" : flashClass === "flash-down" ? "text-red-400" : "text-zinc-100";
  return (
    <span className={`font-mono text-[11px] font-medium ${colorClass} ${flashClass} rounded transition-colors`}>
      {price ? `${getTickerCurrency(ticker)}${formatNumber(price)}` : "..."}
    </span>
  );
}

// Auth Screen (Login/Register)
function AuthScreen({ onAuth }) {
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [finnhubKey, setFinnhubKey] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showKeyHelp, setShowKeyHelp] = useState(false);

  const handleSubmit = async () => {
    if (!email || !password) return;
    if (mode === "register" && !name) return;
    setError("");
    setLoading(true);
    try {
      let data;
      if (mode === "login") {
        data = await authAPI.login(email, password);
      } else {
        data = await authAPI.register(email, password, name, finnhubKey);
      }
      localStorage.setItem("apex_token", data.token);
      localStorage.setItem("apex_user", JSON.stringify(data.user));
      onAuth(data.user);
    } catch (e) {
      const msg = e.response?.data?.detail || "Connection error";
      setError(msg);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-slate-900" data-testid="auth-screen">
      <div className="absolute inset-0 bg-[linear-gradient(rgba(6,182,212,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(6,182,212,0.02)_1px,transparent_1px)] bg-[size:50px_50px]" />
      <div className="glass-card rounded-2xl p-10 w-full max-w-md animate-fade-in relative z-10">
        <div className="text-center mb-8">
          <div className="w-20 h-20 bg-gradient-to-r from-cyan-300 to-blue-500 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-glow">
            <TrendingUp className="w-10 h-10 text-slate-900" />
          </div>
          <h1 className="font-display text-4xl font-bold text-white tracking-tight">MTrades</h1>
          <p className="text-slate-400 text-sm mt-1 font-medium tracking-widest uppercase">Trading Terminal</p>
        </div>

        {/* Mode Toggle */}
        <div className="flex bg-slate-700/50 p-1 rounded-xl mb-6">
          <button onClick={() => { setMode("login"); setError(""); }}
            className="flex-1 py-2.5 text-sm font-semibold rounded-lg transition-all bg-gradient-to-r from-cyan-300 to-blue-500 text-slate-900 shadow-glow"
            data-testid="auth-mode-login">
            Connexion
          </button>
        </div>

        {error && (
          <div className="mb-4 px-4 py-3 bg-red-500/10 border border-red-500/30 rounded-xl text-red-400 text-sm flex items-center gap-2" data-testid="auth-error">
            <AlertTriangle className="w-4 h-4 flex-shrink-0" />{error}
          </div>
        )}

        <div className="space-y-4">
          {mode === "register" && (
            <div>
              <label className="text-xs text-slate-400 font-medium uppercase tracking-wider mb-2 block">Nom</label>
              <div className="relative">
                <User className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input type="text" value={name} onChange={e => setName(e.target.value)}
                  placeholder="Votre nom" className="w-full pl-11 pr-4 py-3.5 bg-slate-800 border border-slate-600 rounded-xl text-white text-sm focus:border-emerald-500/50"
                  data-testid="register-name" />
              </div>
            </div>
          )}

          <div>
            <label className="text-xs text-slate-400 font-medium uppercase tracking-wider mb-2 block">Email</label>
            <div className="relative">
              <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                placeholder="votre@email.com" className="w-full pl-11 pr-4 py-3.5 bg-slate-800 border border-slate-600 rounded-xl text-white text-sm focus:border-emerald-500/50"
                data-testid="auth-email" />
            </div>
          </div>

          <div>
            <label className="text-xs text-slate-400 font-medium uppercase tracking-wider mb-2 block">Mot de passe</label>
            <div className="relative">
              <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input type={showPassword ? "text" : "password"} value={password} onChange={e => setPassword(e.target.value)}
                onKeyDown={e => e.key === "Enter" && handleSubmit()}
                placeholder="Mot de passe" className="w-full pl-11 pr-11 py-3.5 bg-slate-800 border border-slate-600 rounded-xl text-white text-sm focus:border-emerald-500/50"
                data-testid="auth-password" />
              <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-4 top-1/2 -translate-y-1/2">
                {showPassword ? <EyeOff className="w-4 h-4 text-slate-400" /> : <Eye className="w-4 h-4 text-slate-400" />}
              </button>
            </div>
          </div>

          {mode === "register" && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs text-slate-400 font-medium uppercase tracking-wider">Finnhub API Key</label>
                <button onClick={() => setShowKeyHelp(!showKeyHelp)} className="text-xs text-emerald-400 hover:text-emerald-300">
                  {showKeyHelp ? "Masquer" : "Comment obtenir ?"}
                </button>
              </div>
              {showKeyHelp && (
                <div className="gradient-green rounded-xl p-4 mb-3">
                  {[["1", "Allez sur", "finnhub.io"],["2", "Cliquez", "Get free API key"],["3", "Inscrivez-vous", "(30 secondes)"],["4", "Copiez la clé", "et collez ci-dessous"]].map(([num, text, highlight]) => (
                    <div key={num} className="flex items-center gap-3 mb-1.5 last:mb-0">
                      <span className="w-5 h-5 bg-cyan-500 text-white rounded-full flex items-center justify-center text-[10px] font-bold">{num}</span>
                      <span className="text-slate-200 text-xs">{text} <span className="text-emerald-400 font-semibold">{highlight}</span></span>
                    </div>
                  ))}
                </div>
              )}
              <div className="relative">
                <Key className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input type="text" value={finnhubKey} onChange={e => setFinnhubKey(e.target.value)}
                  placeholder="Clé Finnhub (optionnel, ajoutez plus tard)" className="w-full pl-11 pr-4 py-3.5 bg-slate-800 border border-slate-600 rounded-xl text-white font-mono text-sm focus:border-emerald-500/50"
                  data-testid="register-finnhub-key" />
              </div>
            </div>
          )}
        </div>

        <button onClick={handleSubmit} disabled={loading || !email || !password || (mode === "register" && !name)}
          className="w-full py-4 mt-6 bg-gradient-to-r from-cyan-300 to-blue-500 text-slate-900 font-bold text-sm rounded-xl transition-all disabled:opacity-40 shadow-glow hover:shadow-glow-lg flex items-center justify-center gap-2 hover:from-cyan-200 hover:to-blue-400"
          data-testid="auth-submit">
          {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : null}
          {mode === "login" ? "Se connecter" : "Créer mon compte"} <ChevronRight className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
}

// Sector Icon
const SectorIcon = ({ sector }) => {
  const icons = { "Tech": Cpu, "AI/GPU": Cpu, "Semis": Cpu, "Defense": Shield, "Oil": Fuel, "Energy": Fuel, "Bitcoin": Bitcoin, "Crypto": Bitcoin, "Mining": Bitcoin, "Space": Rocket, "Satellite": Rocket, "Nuclear": Atom, "Fintech": Building2 };
  const Icon = Object.entries(icons).find(([key]) => sector?.includes(key))?.[1] || BarChart3;
  return <Icon className="w-4 h-4" />;
};

// Signal Badge component
const SignalBadge = ({ signal }) => {
  const styles = {
    "STRONG_BUY": "bg-cyan-500/25 text-cyan-300 border-cyan-500/40",
    "BUY": "bg-cyan-500/15 text-cyan-400 border-cyan-500/25",
    "HOLD": "bg-blue-500/20 text-blue-400 border-blue-500/30",
    "SELL": "bg-red-500/20 text-red-400 border-red-500/30",
    "STRONG_SELL": "bg-red-500/30 text-red-300 border-red-500/50",
  };
  return (
    <span className={`px-3 py-1 rounded text-xs font-bold uppercase border ${styles[signal] || "bg-slate-700 text-slate-300 border-slate-600"}`}>
      {signal?.replace("_", " ")}
    </span>
  );
};

// Market Status Badge
const MarketStatusBadge = ({ status, info }) => {
  const styles = {
    "OPEN": "bg-cyan-500/15 text-cyan-400 border-cyan-500/25",
    "CLOSED": "bg-red-500/20 text-red-400 border-red-500/30",
    "PRE_MARKET": "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
    "AFTER_HOURS": "bg-orange-500/20 text-orange-400 border-orange-500/30",
  };
  return (
    <div className="flex items-center gap-2">
      <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase border ${styles[status] || "bg-slate-700 text-slate-300"}`}>
        {status === "OPEN" ? <><CircleDot className="inline w-3 h-3 mr-1 animate-pulse" />{status}</> : status}
      </span>
      {info && <span className="text-[10px] text-slate-400">{info}</span>}
    </div>
  );
};

// Position Modal Component
function PositionModal({ isOpen, onClose, position, onSave, onDelete, mode = "add", defaultOwner = "Helmi", supportResistance = {}, prices = {} }) {
  const [form, setForm] = useState({
    name: "",
    ticker: "",
    qty: "",
    avg: "",
    stop_loss: "",
    broker: "Trading 212",
    owner: defaultOwner,
    sector: "",
    flag: "🇺🇸",
    strategy: "LT"
  });

  useEffect(() => {
    if (position && mode === "edit") {
      setForm({
        name: position.name || "",
        ticker: position.ticker || "",
        qty: position.qty?.toString() || "",
        avg: position.avg?.toString() || "",
        stop_loss: position.stop_loss?.toString() || "",
        broker: position.broker || "Trading 212",
        owner: position.owner || defaultOwner,
        sector: position.sector || "",
        flag: position.flag || "🇺🇸",
        strategy: position.strategy || "LT"
      });
    } else if (mode === "add") {
      setForm({
        name: "",
        ticker: "",
        qty: "",
        avg: "",
        stop_loss: "",
        broker: "Trading 212",
        owner: defaultOwner,
        sector: "",
        flag: "🇺🇸",
        strategy: "LT"
      });
    }
  }, [position, mode, isOpen, defaultOwner]);

  if (!isOpen) return null;

  const handleSubmit = () => {
    if (!form.ticker || !form.qty || !form.avg) return;
    onSave({
      ...form,
      ticker: form.ticker.toUpperCase(),
      qty: parseFloat(form.qty),
      avg: parseFloat(form.avg),
      stop_loss: form.stop_loss ? parseFloat(form.stop_loss) : null
    });
  };

  const brokers = ["Millennium", "Trading 212", "Soc. Générale", "Trade Republic", "DEGIRO", "Interactive Brokers", "Autre"];
  const flags = [
    { flag: "🇺🇸", label: "US" },
    { flag: "🇫🇷", label: "France" },
    { flag: "🇩🇪", label: "Germany" },
    { flag: "🇬🇧", label: "UK" },
    { flag: "🇪🇺", label: "Europe" },
  ];

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-slate-800 border border-slate-600 rounded-2xl p-6 w-full max-w-lg animate-fade-in" onClick={e => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-6">
          <h2 className="font-display text-xl font-bold">
            {mode === "add" ? "Ajouter une position" : "Modifier la position"}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-slate-700 rounded-lg transition-colors">
            <X className="w-5 h-5 text-slate-300" />
          </button>
        </div>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-slate-400 font-medium uppercase tracking-wider mb-2 block">Ticker *</label>
              <input type="text" value={form.ticker} onChange={e => setForm({...form, ticker: e.target.value.toUpperCase()})}
                placeholder="NVDA" className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white font-mono"/>
            </div>
            <div>
              <label className="text-xs text-slate-400 font-medium uppercase tracking-wider mb-2 block">Nom</label>
              <input type="text" value={form.name} onChange={e => setForm({...form, name: e.target.value})}
                placeholder="Nvidia" className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white"/>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="text-xs text-slate-400 font-medium uppercase tracking-wider mb-2 block">Quantité *</label>
              <input type="number" step="0.01" value={form.qty} onChange={e => setForm({...form, qty: e.target.value})}
                placeholder="100" className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white font-mono"/>
            </div>
            <div>
              <label className="text-xs text-slate-400 font-medium uppercase tracking-wider mb-2 block">Prix moyen *</label>
              <input type="number" step="0.01" value={form.avg} onChange={e => setForm({...form, avg: e.target.value})}
                placeholder="150.00" className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white font-mono"/>
            </div>
            <div>
              <label className="text-xs text-red-400/80 font-medium uppercase tracking-wider mb-2 block flex items-center gap-1">Stop Loss</label>
              <div className="relative">
                <input type="number" step="0.01" value={form.stop_loss} onChange={e => setForm({...form, stop_loss: e.target.value})}
                  placeholder={(() => {
                    const t = form.ticker?.toUpperCase();
                    const sr = supportResistance[t];
                    const avg = parseFloat(form.avg);
                    if (sr?.support) return `Support: ${sr.support}`;
                    if (avg) return `Suggéré: ${(avg * 0.9).toFixed(2)}`;
                    return "120.00";
                  })()}
                  className="w-full px-4 py-3 bg-slate-700 border border-red-500/30 rounded-lg text-white font-mono pr-16" data-testid="stop-loss-input"/>
                <button type="button" onClick={() => {
                  const t = form.ticker?.toUpperCase();
                  const sr = supportResistance[t];
                  const avg = parseFloat(form.avg);
                  const price = prices[t];
                  if (sr?.support) { setForm({...form, stop_loss: sr.support.toString()}); }
                  else if (avg) { setForm({...form, stop_loss: (avg * 0.9).toFixed(2)}); }
                }}
                  className="absolute right-1.5 top-1.5 px-2 py-1.5 bg-red-500/20 text-red-400 text-[9px] font-bold rounded hover:bg-red-500/30 transition-colors"
                  data-testid="suggest-sl-btn">
                  AUTO
                </button>
              </div>
              {form.ticker && (() => {
                const t = form.ticker?.toUpperCase();
                const sr = supportResistance[t];
                const avg = parseFloat(form.avg);
                const sl = parseFloat(form.stop_loss);
                const loss = sl && avg ? (((sl - avg) / avg) * 100).toFixed(1) : null;
                return (
                  <div className="mt-1.5 flex items-center gap-2 text-[9px]">
                    {sr?.support && <span className="text-emerald-400/70">Support: ${sr.support}</span>}
                    {loss && <span className="text-red-400/70">Risque: {loss}%</span>}
                    {!sr?.support && avg > 0 && <span className="text-slate-500">Suggéré: -10% = ${(avg * 0.9).toFixed(2)}</span>}
                  </div>
                );
              })()}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-slate-400 font-medium uppercase tracking-wider mb-2 block">Broker</label>
              <select value={form.broker} onChange={e => setForm({...form, broker: e.target.value})}
                className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white">
                {brokers.map(b => <option key={b} value={b}>{b}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-slate-400 font-medium uppercase tracking-wider mb-2 block">Propriétaire</label>
              <select value={form.owner} onChange={e => setForm({...form, owner: e.target.value})}
                className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white">
                <option value="Helmi">Helmi</option>
                <option value="Anne-Laure">Anne-Laure</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-slate-400 font-medium uppercase tracking-wider mb-2 block">Secteur</label>
              <input type="text" value={form.sector} onChange={e => setForm({...form, sector: e.target.value})}
                placeholder="Tech / IA" className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white"/>
            </div>
            <div>
              <label className="text-xs text-slate-400 font-medium uppercase tracking-wider mb-2 block">Stratégie</label>
              <div className="flex gap-2">
                <button onClick={() => setForm({...form, strategy: "LT"})} data-testid="strategy-lt-btn"
                  className={`flex-1 px-3 py-2.5 rounded-lg text-sm font-bold transition-all ${form.strategy === "LT" ? "bg-blue-500/20 border border-blue-500/50 text-blue-400" : "bg-slate-700 border border-slate-600 text-slate-400"}`}>
                  LT Long Term
                </button>
                <button onClick={() => setForm({...form, strategy: "ST"})} data-testid="strategy-st-btn"
                  className={`flex-1 px-3 py-2.5 rounded-lg text-sm font-bold transition-all ${form.strategy === "ST" ? "bg-amber-500/20 border border-amber-500/50 text-amber-400" : "bg-slate-700 border border-slate-600 text-slate-400"}`}>
                  ST Swing
                </button>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-slate-400 font-medium uppercase tracking-wider mb-2 block">Pays</label>
              <div className="flex gap-2">
                {flags.map(f => (
                  <button key={f.flag} onClick={() => setForm({...form, flag: f.flag})}
                    className={`px-3 py-2 rounded-lg text-xl transition-all ${form.flag === f.flag ? "bg-cyan-500/15 border border-cyan-500/25" : "bg-slate-700 border border-slate-600"}`}>
                    {f.flag}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-between mt-6 pt-4 border-t border-slate-700">
          {mode === "edit" && onDelete && (
            <button onClick={() => onDelete(position.id)} className="px-4 py-2 bg-red-500/20 text-red-400 border border-red-500/30 rounded-lg font-semibold flex items-center gap-2">
              <Trash2 className="w-4 h-4" />Supprimer
            </button>
          )}
          <div className={`flex gap-3 ${mode === "add" ? "w-full justify-end" : "ml-auto"}`}>
            <button onClick={onClose} className="px-4 py-2 bg-slate-700 text-slate-300 border border-slate-600 rounded-lg font-semibold">
              Annuler
            </button>
            <button onClick={handleSubmit} disabled={!form.ticker || !form.qty || !form.avg}
              className="px-6 py-2 bg-gradient-to-r from-cyan-300 to-blue-500 text-slate-900 rounded-lg font-semibold flex items-center gap-2 disabled:opacity-50 shadow-glow">
              <Save className="w-4 h-4" />{mode === "add" ? "Ajouter" : "Sauvegarder"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Main App
export default function App() {
  const [user, setUser] = useState(null);
  const [apiKey, setApiKey] = useState("");
  const [tab, setTab] = useState("portfolio");
  const [owner, setOwner] = useState("Helmi");
  const [prices, setPrices] = useState({});
  const [prevPrices, setPrevPrices] = useState({});
  const [dailyChanges, setDailyChanges] = useState({});
  const [athData, setAthData] = useState({});
  const [supportResistance, setSupportResistance] = useState({});
  const [sparklines, setSparklines] = useState({});
  const [loading, setLoading] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [news, setNews] = useState([]);
  const [showSettings, setShowSettings] = useState(false);
  const [portfolioCmd, setPortfolioCmd] = useState("");
  const [portfolioCmdLoading, setPortfolioCmdLoading] = useState(false);
  const [portfolioCmdResult, setPortfolioCmdResult] = useState(null);
  const [pendingDeepDiveTicker, setPendingDeepDiveTicker] = useState("");
  const [scanChatInput, setScanChatInput] = useState("");
  const [settingsKey, setSettingsKey] = useState("");
  const [marketNews, setMarketNews] = useState([]);
  const [insiderData, setInsiderData] = useState([]);
  // Briefing state — persists across tab changes
  const [briefingCache, setBriefingCache] = useState({ briefing: "", sources: [], loaded: false });
  
  // Scanner State
  const [scanResults, setScanResults] = useState([]);
  const [scanning, setScanning] = useState(false);
  const [scanProgress, setScanProgress] = useState({ current: 0, total: 0, sector: "" });
  const [selectedSectors, setSelectedSectors] = useState(["AI_SEMIS", "DEFENSE", "CRYPTO"]);
  const [scanLoadedFromDb, setScanLoadedFromDb] = useState(false);
  const [massiveScan, setMassiveScan] = useState({ results: [], loading: false, scannedAt: null });
  const [massiveFilter, setMassiveFilter] = useState("ALL");
  const [massiveSignalFilter, setMassiveSignalFilter] = useState("ALL");
  const [alertHistory, setAlertHistory] = useState([]);
  const [watchlistAlerts, setWatchlistAlerts] = useState([]);
  const [watchlistAlertsSummary, setWatchlistAlertsSummary] = useState(null);
  const [watchlistAlertsLoading, setWatchlistAlertsLoading] = useState(false);
  const [topPicks, setTopPicks] = useState([]);
  const [topPicksAI, setTopPicksAI] = useState(null);
  const [topPicksAILoading, setTopPicksAILoading] = useState(false);
  const [swingPicks, setSwingPicks] = useState([]);
  const [marketTiming, setMarketTiming] = useState(null);
  const [marketTimingLoading, setMarketTimingLoading] = useState(false);
  const [sectorHeatmap, setSectorHeatmap] = useState(null);
  const [sectorHeatmapLoading, setSectorHeatmapLoading] = useState(false);
  const [sectorDrilldown, setSectorDrilldown] = useState(null);
  const [sectorDrilldownLoading, setSectorDrilldownLoading] = useState(false);
  const [selectedSector, setSelectedSector] = useState(null);
  const [scanMode, setScanMode] = useState("massive"); // "massive" or "sector"
  
  // Auto-load massive scan results when scanner tab opens
  useEffect(() => {
    if (tab === "scanner" && scanMode === "massive" && massiveScan.results.length === 0 && !massiveScan.loading) {
      (async () => {
        setMassiveScan(prev => ({ ...prev, loading: true }));
        const data = await massiveScanAPI.getResults();
        if (data.results && data.results.length > 0) {
          setMassiveScan({ results: data.results, loading: false, scannedAt: data.scanned_at });
        } else {
          const res = await massiveScanAPI.triggerScan();
          if (res.ok) {
            const fresh = await massiveScanAPI.getResults();
            setMassiveScan({ results: fresh.results || [], loading: false, scannedAt: fresh.scanned_at });
          } else {
            setMassiveScan(prev => ({ ...prev, loading: false }));
          }
        }
      })();
    }
    if (tab === "scanner" && scanMode === "massive" && alertHistory.length === 0) {
      massiveScanAPI.getAlertHistory().then(data => {
        if (data.alerts) setAlertHistory(data.alerts);
      });
    }
  }, [tab, scanMode]);

  // Load watchlist alerts + top picks + market timing on startup
  useEffect(() => {
    if (owner) {
      watchlistAPI.getAlerts(owner).then(data => {
        if (data && data.alerts) {
          setWatchlistAlerts(data.alerts);
          setWatchlistAlertsSummary(data.summary || { buy_zone: 0, near_buy: 0, oversold: 0, strong_signal: 0, big_drop: 0, total_stocks: data.total_stocks });
        }
        if (data && data.top_picks) {
          setTopPicks(data.top_picks);
        }
        if (data && data.swing_picks) {
          setSwingPicks(data.swing_picks);
        }
        if (data && data.ai_analysis) {
          setTopPicksAI(data.ai_analysis);
        }
      }).catch(() => {});
      // Load market timing
      watchlistAPI.getMarketTiming().then(data => {
        if (data && data.timing_score !== undefined) setMarketTiming(data);
      }).catch(() => {});
      // Load sector heatmap
      watchlistAPI.getSectorHeatmap(owner).then(data => {
        if (data && data.sectors) setSectorHeatmap(data);
      }).catch(() => {});
      // Load economic calendar
      finnhubAPI.getEconomicCalendar().then(data => {
        if (data?.events) setEconomicEvents(data.events);
      }).catch(() => {});
      // Load flash news (macro / geopolitical high-impact headlines)
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/briefing/flash-news?limit=10`)
        .then(r => r.ok ? r.json() : null)
        .then(d => { if (d?.items) setFlashNews(d.items); })
        .catch(() => {});
    }
  }, [owner]);

  const handleEcoEventClick = useCallback(async (evt) => {
    setSelectedEcoEvent(evt);
    setEcoAnalysis(null);
    setEcoAnalysisLoading(true);
    try {
      const data = await finnhubAPI.analyzeEconomicEvent(evt);
      setEcoAnalysis(data);
    } catch (e) {
      setEcoAnalysis({ error: e.message });
    } finally {
      setEcoAnalysisLoading(false);
    }
  }, []);

  // Price Alert Handler
  const handleSetPriceAlert = useCallback(async (alertData) => {
    if (!owner) return;
    try {
      const result = await priceAlertsAPI.create({
        ...alertData,
        owner,
      });
      if (result.ok) {
        toast.success(`Alerte créée: ${alertData.ticker} à ${getTickerCurrency(alertData.ticker)}${alertData.target_price}`);
        // Refresh active alerts
        const data = await priceAlertsAPI.getAll(owner);
        setPriceAlerts(data.alerts || []);
      } else {
        toast.error(result.error || "Erreur création alerte");
      }
    } catch (e) {
      toast.error("Erreur création alerte");
    }
  }, [owner]);

  // Poll triggered alerts every 60 seconds — DEFERRED: only after prices are loaded
  useEffect(() => {
    if (!owner || Object.keys(prices).length === 0) return;
    const fetchAlerts = async () => {
      try {
        const [allData, trigData] = await Promise.all([
          priceAlertsAPI.getAll(owner),
          priceAlertsAPI.getTriggered(owner),
        ]);
        setPriceAlerts(allData.alerts || []);
        const newTriggered = trigData.alerts || [];
        
        // Show toast for newly triggered alerts
        newTriggered.forEach(a => {
          const key = `${a.ticker}_${a.created_at}`;
          if (!prevTriggeredRef.current.has(key)) {
            toast.success(
              `${a.ticker} a atteint ${getTickerCurrency(a.ticker)}${a.triggered_price} ! (cible: ${getTickerCurrency(a.ticker)}${a.target_price})`,
              { duration: 8000 }
            );
          }
        });
        prevTriggeredRef.current = new Set(newTriggered.map(a => `${a.ticker}_${a.created_at}`));
        setTriggeredAlerts(newTriggered);
      } catch (e) {
        // silent
      }
    };
    fetchAlerts();
    const interval = setInterval(fetchAlerts, 60000);
    return () => clearInterval(interval);
  }, [owner, Object.keys(prices).length > 0]); // eslint-disable-line

  const scanWatchlistOpportunities = useCallback(async () => {
    setWatchlistAlertsLoading(true);
    try {
      await watchlistAPI.scanOpportunities(owner);
      toast.info("Scan watchlist lancé... peut prendre 1-3 min (univers étendu)");
      let attempts = 0;
      const maxAttempts = 36; // 36 * 10s = 6 min — couvre largement le scan EU + US (119 tickers)
      const poll = setInterval(async () => {
        attempts++;
        try {
          const data = await watchlistAPI.getAlerts(owner);
          const now = new Date();
          const updatedAt = data.updated_at ? new Date(data.updated_at) : null;
          if (updatedAt && (now - updatedAt) < 300000 && data.scanned > 0) {
            setWatchlistAlerts(data.alerts || []);
            setWatchlistAlertsSummary(data.summary || {});
            if (data.top_picks) setTopPicks(data.top_picks);
            if (data.swing_picks) setSwingPicks(data.swing_picks);
            setTopPicksAI(null);
            setWatchlistAlertsLoading(false);
            clearInterval(poll);
            if (data.top_picks?.length > 0 || data.swing_picks?.length > 0) {
              toast.success(`${data.top_picks?.length || 0} top picks + ${data.swing_picks?.length || 0} swing sur ${data.total_scanned || data.scanned || "200+"} actions (score /120)`);
            } else if (data.alerts?.length > 0) {
              toast.success(`${data.alerts.length} alertes sur ${data.scanned} actions`);
            } else {
              toast.info(`Aucune alerte — ${data.scanned} actions scannées`);
            }
          }
        } catch (e) {}
        if (attempts >= maxAttempts) {
          clearInterval(poll);
          setWatchlistAlertsLoading(false);
          // 🔧 2026-05-22: silencieux quand le scan finit par arriver dans le cache.
          // L'erreur tonitruante était souvent fausse (le scan finit derrière).
          toast.info("Scan en cours en arrière-plan — vérifie dans quelques minutes.");
        }
      }, 10000); // 10s plutôt que 5s = 50% moins d'appels API
    } catch (e) {
      toast.error("Erreur lors du lancement du scan");
      setWatchlistAlertsLoading(false);
    }
  }, [owner]);

  const analyzeTopPicksAI = useCallback(async () => {
    setTopPicksAILoading(true);
    try {
      const result = await watchlistAPI.analyzeTopPicksAI(owner);
      setTopPicksAI(result);
      toast.success("Analyse IA terminée !");
    } catch (e) {
      toast.error("Erreur lors de l'analyse IA");
    } finally {
      setTopPicksAILoading(false);
    }
  }, [owner]);

  const refreshMarketTiming = useCallback(async () => {
    setMarketTimingLoading(true);
    try {
      const data = await watchlistAPI.getMarketTiming();
      if (data && data.timing_score !== undefined) setMarketTiming(data);
    } catch (e) {}
    setMarketTimingLoading(false);
  }, []);

  const refreshSectorHeatmap = useCallback(async () => {
    if (!owner) return;
    setSectorHeatmapLoading(true);
    try {
      const data = await watchlistAPI.getSectorHeatmap(owner, true);
      if (data && data.sectors) {
        setSectorHeatmap(data);
        toast.success(`Secteurs mis à jour (${data.sectors?.length || 0} secteurs)`);
      }
    } catch (e) {
      toast.error("Erreur de rafraîchissement des secteurs");
    }
    setSectorHeatmapLoading(false);
  }, [owner]);

  const openSectorDrilldown = useCallback(async (sectorEtf, sectorName) => {
    setSelectedSector({ etf: sectorEtf, name: sectorName });
    setSectorDrilldownLoading(true);
    setSectorDrilldown(null);
    try {
      const data = await watchlistAPI.getSectorStocks(sectorEtf, owner);
      if (data?.error === "timeout") {
        toast.error("Chargement trop lent — réessayez dans 1 min");
      }
      setSectorDrilldown(data);
    } catch (e) {
      toast.error("Erreur lors du chargement du secteur");
    }
    setSectorDrilldownLoading(false);
  }, [owner]);

  const closeSectorDrilldown = useCallback(() => {
    setSelectedSector(null);
    setSectorDrilldown(null);
  }, []);

  // Algo State
  const [algoTicker, setAlgoTicker] = useState("NVDA");
  const [algoAnalysis, setAlgoAnalysis] = useState(null);
  const [stockPerf, setStockPerf] = useState({});
  const [stockFundamentals, setStockFundamentals] = useState(null);
  const [fundamentalsPro, setFundamentalsPro] = useState(null);
  const [backtestConclusion, setBacktestConclusion] = useState("");
  const [algoLoading, setAlgoLoading] = useState(false);
  const [algoHistory, setAlgoHistory] = useState([]);
  const [algoTopRated, setAlgoTopRated] = useState([]);
  const [dailyTopPicks, setDailyTopPicks] = useState([]);
  const [pullbackOpps, setPullbackOpps] = useState([]);
  const [pullbackLoading, setPullbackLoading] = useState(false);
  const [scannerTopPicks, setScannerTopPicks] = useState([]);
  const [sellSignals, setSellSignals] = useState({});
  const [analystRatings, setAnalystRatings] = useState({});
  const [backtestResult, setBacktestResult] = useState(null);
  const [backtestStrategy, setBacktestStrategy] = useState("RSI_MEAN_REVERSION");
  const [portfolioOptimization, setPortfolioOptimization] = useState(null);
  const [chartTimeframe, setChartTimeframe] = useState("3M");
  const [chartData, setChartData] = useState(null);
  const [chartLoading, setChartLoading] = useState(false);
  const [sortCol, setSortCol] = useState(null);
  const [sortDir, setSortDir] = useState("desc");
  
  // AI State
  const [aiMessage, setAiMessage] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [aiInput, setAiInput] = useState("");
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [expertResults, setExpertResults] = useState(null);
  const [smartAnalyzerTicker, setSmartAnalyzerTicker] = useState(null);  // ticker à analyser via Claude
  const [smartPortfolioOpen, setSmartPortfolioOpen] = useState(false);   // modal analyse portefeuille
  const [smartFinderOpen, setSmartFinderOpen] = useState(false);          // modal "trouve une action"
  const [chatHistory, setChatHistory] = useState([]);
  const [debateResults, setDebateResults] = useState(null);
  const [debateLoading, setDebateLoading] = useState(false);
  const [synthesisResult, setSynthesisResult] = useState(null);
  const [synthesisLoading, setSynthesisLoading] = useState(false);
  const [followupMessages, setFollowupMessages] = useState([]);
  const [followupInput, setFollowupInput] = useState("");
  const [followupLoading, setFollowupLoading] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [listenTarget, setListenTarget] = useState(null);
  const recognitionRef = useRef(null);
  const [chatImages, setChatImages] = useState([]);  // [{base64, content_type, preview}]
  const imageInputRef = useRef(null);
  const [conversationHistory, setConversationHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [lastPrompt, setLastPrompt] = useState("");
  const [lastContext, setLastContext] = useState("");
  
  // Portfolio Management State
  const [showPositionModal, setShowPositionModal] = useState(false);
  const [editingPosition, setEditingPosition] = useState(null);
  const [modalMode, setModalMode] = useState("add");
  const [dbPortfolio, setDbPortfolio] = useState([]);
  const [marketStatus, setMarketStatus] = useState({});
  const [economicEvents, setEconomicEvents] = useState([]);
  const [flashNews, setFlashNews] = useState([]);
  const [selectedEcoEvent, setSelectedEcoEvent] = useState(null);
  const [ecoAnalysis, setEcoAnalysis] = useState(null);
  const [ecoAnalysisLoading, setEcoAnalysisLoading] = useState(false);
  const [strategyFilter, setStrategyFilter] = useState("ALL"); // ALL, LT, ST
  const [earningsData, setEarningsData] = useState({}); // {ticker: {date, eps_estimate}}
  const [valuationData, setValuationData] = useState({}); // {ticker: {fair_value, coefficient, ...}}
  const [convictionScores, setConvictionScores] = useState({}); // {ticker: {score, grade, catalysts, headline, ...}}
  const [sparklineData, setSparklineData] = useState({}); // {ticker: {data: [], change_pct: number}}
  const [postMarketData, setPostMarketData] = useState({}); // {ticker: {pm, pm_d, pm_dp}}
  const [t212Syncing, setT212Syncing] = useState(false);
  const [t212Account, setT212Account] = useState(null);
  const [realizedPnl, setRealizedPnl] = useState(null);
  const [showSellDialog, setShowSellDialog] = useState(null); // {positionId, position, mode: 'delete'|'partial'}
  
  const intervalRef = useRef(null);

  // Watchlist State
  const [watchlist, setWatchlist] = useState([]);
  const [watchlistGenerating, setWatchlistGenerating] = useState(false);
  const [watchlistGeneratedAt, setWatchlistGeneratedAt] = useState(null);
  const [watchlistRegionFilter, setWatchlistRegionFilter] = useState("ALL");
  const [addTickerInput, setAddTickerInput] = useState("");
  const [watchlistLivePrices, setWatchlistLivePrices] = useState({});
  const prevBuyZoneRef = useRef({});
  const [watchlistAlertCount, setWatchlistAlertCount] = useState(0);
  const [flowAlertCount, setFlowAlertCount] = useState(0);

  // Momentum cached state (persists across tab switches)
  const [momentumData, setMomentumData] = useState(null);
  // Capital Flow cached state
  const [capitalFlowData, setCapitalFlowData] = useState(null);
  // Performance cached state
  const [perfSnapshots, setPerfSnapshots] = useState([]);

  // Price Alerts State
  const [priceAlerts, setPriceAlerts] = useState([]);
  const [triggeredAlerts, setTriggeredAlerts] = useState([]);
  const [showAlertPanel, setShowAlertPanel] = useState(false);
  const prevTriggeredRef = useRef(new Set());

  // Exchange Rates State (currency -> USD conversion factor)
  const [exchangeRates, setExchangeRates] = useState({ 
    USD: 1.0, EUR: 1.08, GBP: 1.27, CHF: 1.10, DKK: 0.145, SEK: 0.095, NOK: 0.093, 
    JPY: 0.0067, KRW: 0.00073, HKD: 0.128, AUD: 0.65, INR: 0.012 
  });

  // 2026-06-17 — Broker-accurate price: use ONLY the regular market close (no pre/post overlay).
  // Reason: broker apps (Millennium, Soc.Gen, Trading 212) display their portfolio totals
  // using the last regular-session close — so to let the user reconcile their balance with
  // their broker app, we must do the same. Pre/post-market data is still shown in dedicated
  // widgets (sniper bot, alerts) but never in portfolio valuation.
  const getPrice = useCallback((pos) => {
    return prices[pos.ticker] || pos.avg;
  }, [prices]);

  // Voice recognition (Speech-to-Text)
  const toggleVoice = useCallback((target) => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      toast.error("Votre navigateur ne supporte pas la reconnaissance vocale (utilisez Chrome ou Edge)");
      return;
    }
    if (isListening) {
      recognitionRef.current?.stop();
      setIsListening(false);
      setListenTarget(null);
      return;
    }
    const recognition = new SpeechRecognition();
    recognition.lang = "fr-FR";
    recognition.continuous = false;
    recognition.interimResults = true;
    recognitionRef.current = recognition;
    setListenTarget(target);
    setIsListening(true);

    let fullText = "";
    const setter = target === "main" ? setAiInput : target === "portfolio" ? setPortfolioCmd : setFollowupInput;

    recognition.onresult = (e) => {
      let transcript = "";
      for (let i = 0; i < e.results.length; i++) {
        transcript += e.results[i][0].transcript;
      }
      setter(prev => {
        const base = fullText || "";
        return base + transcript;
      });
    };
    recognition.onerror = (e) => {
      console.error("Speech error:", e.error);
      if (e.error === "not-allowed") {
        toast.error("Accès au micro refusé. Autorisez le micro dans les paramètres du navigateur.");
      } else if (e.error === "no-speech") {
        toast.info("Aucune voix détectée, réessayez");
      } else {
        toast.error(`Erreur micro: ${e.error}`);
      }
      setIsListening(false);
      setListenTarget(null);
    };
    recognition.onend = () => {
      // Auto-restart if user hasn't manually stopped
      if (recognitionRef.current && isListening) {
        try {
          // Save current text before restarting
          setter(prev => { fullText = prev; return prev; });
          recognition.start();
          return;
        } catch (e) { /* ignore restart errors */ }
      }
      setIsListening(false);
      setListenTarget(null);
    };
    try {
      recognition.start();
      toast.info("Parlez maintenant...");
    } catch (e) {
      toast.error("Impossible de démarrer le micro");
      setIsListening(false);
      setListenTarget(null);
    }
  }, [isListening]);

  // Load portfolio from DB (auto-init from static data if empty)
  // 🆕 Always commit the fetched array to state — even if same length — so React picks up
  // mutations (price updates, new bot trades, edits). The previous guard
  // `if (positions.length > 0) setDbPortfolio(...)` could leave the UI stuck on an empty
  // initial render until a hard refresh, which was the "needs refresh to see data" bug.
  const loadPortfolio = useCallback(async () => {
    try {
      let positions = await portfolioAPI.getAllPositions();
      if (!positions || positions.length === 0) {
        // Auto-initialize from static PORTFOLIO
        await portfolioAPI.initDefaultPortfolio();
        positions = await portfolioAPI.getAllPositions();
      }
      // Auto-classify positions without strategy
      const needsClassify = positions && positions.some(p => !p.strategy);
      if (needsClassify) {
        await portfolioAPI.autoClassifyStrategies();
        positions = await portfolioAPI.getAllPositions();
      }
      // Always commit to state — even an empty array is a valid state and prevents stale UI.
      // A new array reference (spread) guarantees React detects the change and re-renders.
      const safe = Array.isArray(positions) ? [...positions] : [];
      setDbPortfolio(safe);
      return safe;
    } catch (e) {
      console.error("Error loading portfolio:", e);
      // Don't wipe existing state on transient errors — just return whatever we last had.
      return [];
    }
  }, []);

  // Load realized P&L
  const loadRealizedPnl = useCallback(async () => {
    try {
      const data = await portfolioAPI.getRealizedPnl(owner);
      setRealizedPnl(data);
    } catch (e) {
      console.error("Error loading realized PnL:", e);
    }
  }, [owner]);

  // Load market status
  const loadMarketStatus = useCallback(async () => {
    try {
      const status = await marketAPI.getStatus();
      setMarketStatus(status);
    } catch (e) {
      console.error("Error loading market status:", e);
    }
  }, []);

  // Load analyst ratings (Wall Street consensus) for the current owner's portfolio
  const loadAnalystRatings = useCallback(async () => {
    if (!owner) return;
    try {
      const token = localStorage.getItem("apex_token");
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/analyst-ratings/portfolio/${encodeURIComponent(owner)}`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      if (!res.ok) return;
      const data = await res.json();
      setAnalystRatings(data.ratings || {});
    } catch (e) {
      console.error("Error loading analyst ratings:", e);
    }
  }, [owner]);

  useEffect(() => {
    loadAnalystRatings();
    const iv = setInterval(loadAnalystRatings, 6 * 3600 * 1000); // refresh every 6h
    return () => clearInterval(iv);
  }, [loadAnalystRatings]);

  // Add position
  const handleAddPosition = useCallback(async (positionData) => {
    try {
      await portfolioAPI.addPosition(positionData);
      await loadPortfolio();
      setShowPositionModal(false);
    } catch (e) {
      console.error("Error adding position:", e);
    }
  }, [loadPortfolio]);

  // Update position
  const handleUpdatePosition = useCallback(async (positionData) => {
    if (!editingPosition) return;
    try {
      // Detect qty decrease (partial sell)
      const oldQty = editingPosition.qty || 0;
      const newQty = positionData.qty || 0;
      if (newQty < oldQty) {
        // Ask for sell price
        const currentPrice = prices[editingPosition.ticker]?.price || 0;
        const defaultPrice = currentPrice > 0 ? currentPrice.toFixed(2) : "";
        const sellPriceStr = window.prompt(
          `Vente partielle de ${(oldQty - newQty).toFixed(2)} ${editingPosition.ticker}\nPrix de vente ? (laisser vide = prix actuel ${defaultPrice})`,
          defaultPrice
        );
        if (sellPriceStr === null) return; // Cancelled
        const sellPrice = parseFloat(sellPriceStr) || currentPrice;
        positionData.sell_price = sellPrice > 0 ? sellPrice : undefined;
      }
      await portfolioAPI.updatePosition(editingPosition.id, positionData);
      await loadPortfolio();
      loadRealizedPnl();
      setShowPositionModal(false);
      setEditingPosition(null);
    } catch (e) {
      console.error("Error updating position:", e);
    }
  }, [editingPosition, loadPortfolio, loadRealizedPnl, prices]);

  // Delete position
  const handleDeletePosition = useCallback(async (positionId) => {
    const position = dbPortfolio.find(p => p.id === positionId);
    const ticker = position?.ticker || "";
    const qty = position?.qty || 0;
    const currentPrice = prices[ticker] || position?.avg || 0;
    
    try {
      await portfolioAPI.deletePosition(positionId, currentPrice > 0 ? currentPrice : undefined);
      await loadPortfolio();
      loadRealizedPnl();
      setShowPositionModal(false);
      setEditingPosition(null);
      if (currentPrice > 0 && position) {
        const pnl = (currentPrice - (position.avg || 0)) * qty;
        const sign = pnl >= 0 ? "+" : "";
        toast.success(`${ticker} vendu @ ${currentPrice.toFixed(2)} — P&L: ${sign}${pnl.toFixed(0)}€`);
      } else {
        toast.success(`${ticker} supprimé`);
      }
    } catch (e) {
      console.error("Error deleting position:", e);
      toast.error("Erreur suppression");
    }
  }, [loadPortfolio, loadRealizedPnl, dbPortfolio, prices]);

  // Open modal for editing
  const openEditModal = useCallback((position) => {
    setEditingPosition(position);
    setModalMode("edit");
    setShowPositionModal(true);
  }, []);

  // Open modal for adding
  const openAddModal = useCallback(() => {
    setEditingPosition(null);
    setModalMode("add");
    setShowPositionModal(true);
  }, []);

  // Sync Trading 212
  const syncTrading212 = useCallback(async () => {
    setT212Syncing(true);
    try {
      const result = await trading212API.sync(owner);
      toast.success(`Trading 212 : ${result.synced} positions synchronisées`);
      await loadPortfolio();
      // Also fetch account info
      const account = await trading212API.getAccount();
      setT212Account(account);
    } catch (e) {
      toast.error("Erreur sync Trading 212: " + (e.response?.data?.detail || e.message));
    } finally {
      setT212Syncing(false);
    }
  }, [owner, loadPortfolio]);

  // Fetch quotes — BATCH mode (all tickers in one call via yfinance)
  const fetchQuotes = useCallback(async (key, extraPositions = []) => {
    setLoading(true);
    const allTickers = [...new Set([...dbPortfolio.map(p => p.ticker), ...extraPositions.map(p => p.ticker)])];
    const newPrices = {};
    const newDailyChanges = {};
    const newPostMarket = {};
    
    // Single batch call for ALL tickers (yfinance handles US, EU, Asia, Crypto)
    if (allTickers.length > 0) {
      const yfData = await finnhubAPI.getYFinanceBatchQuotes(allTickers, true);
      if (yfData) {
        Object.entries(yfData).forEach(([ticker, data]) => {
          if (data && data.c > 0) {
            newPrices[ticker] = data.c;
            newDailyChanges[ticker] = { d: data.d || 0, dp: data.dp || 0 };
            if (data.pm) newPostMarket[ticker] = { pm: data.pm, pm_d: data.pm_d, pm_dp: data.pm_dp };
            if (data.pre) newPostMarket[ticker] = { ...newPostMarket[ticker], pre: data.pre, pre_d: data.pre_d, pre_dp: data.pre_dp };
          }
        });
      }
    }
    
    setPrevPrices(prev => ({ ...prev, ...prices }));
    setPrices(prev => ({ ...prev, ...newPrices }));
    setDailyChanges(prev => ({ ...prev, ...newDailyChanges }));
    if (Object.keys(newPostMarket).length > 0) setPostMarketData(prev => ({ ...prev, ...newPostMarket }));
    setLastUpdate(new Date());
    setLoading(false);
  }, [prices, dbPortfolio]);

  const fetchCandles = useCallback(async (key, extraPositions = []) => {
    const allTickers = [...new Set([...dbPortfolio.map(p => p.ticker), ...extraPositions.map(p => p.ticker)])];
    const tickers = allTickers.slice(0, 30);
    // Use batch sparklines endpoint instead of sequential candle fetches
    try {
      const sparkData = await finnhubAPI.getSparklinesBatch(tickers);
      if (sparkData) {
        const newSparks = {};
        Object.entries(sparkData).forEach(([ticker, values]) => {
          // Backend returns {data: [...], change_pct: number}
          const arr = Array.isArray(values) ? values : (values && Array.isArray(values.data) ? values.data : null);
          if (arr && arr.length > 0) {
            newSparks[ticker] = arr.slice(-14);
          }
        });
        setSparklines(newSparks);
      }
    } catch (e) {
      console.error("Sparklines batch error:", e);
    }
  }, [dbPortfolio]);

  const fetchNews = useCallback(async (key) => {
    if (!key) return;
    const data = await finnhubAPI.getNews(key);
    if (Array.isArray(data)) setNews(data.slice(0, 12));
    // Also fetch market news
    const mNews = await newsAPI.getMarketNews(key, 15);
    if (Array.isArray(mNews)) setMarketNews(mNews);
  }, []);

  const fetchInsiderData = useCallback(async (key) => {
    if (!key) return;
    const currentPositions = dbPortfolio.length > 0 ? dbPortfolio : PORTFOLIO;
    const tickers = currentPositions.filter(p => p.owner === owner).map(p => p.ticker).filter(t => !t.includes(".")).slice(0, 8);
    if (tickers.length === 0) return;
    const data = await newsAPI.getBulkInsider(tickers, key);
    if (Array.isArray(data)) setInsiderData(data);
  }, [dbPortfolio, owner]);

  // Market Scanner
  useEffect(() => {
    if (scanLoadedFromDb || scanResults.length > 0) return;
    algoAPI.getSavedScan().then(saved => {
      if (saved.results?.length > 0) {
        setScanResults(saved.results);
        toast.success(`${saved.results.length} r\u00e9sultats de scan charg\u00e9s`);
      }
      setScanLoadedFromDb(true);
    });
  }, [scanLoadedFromDb, scanResults.length]);

  const runMarketScan = useCallback(async () => {
    setScanning(true);
    setScanResults([]);
    const allTickers = selectedSectors.flatMap(sector => MARKET_SCAN_TICKERS[sector] || []);
    const total = allTickers.length;
    setScanProgress({ current: 0, total, sector: "" });
    const results = [];
    let current = 0;
    for (const sectorKey of selectedSectors) {
      const sectorTickers = MARKET_SCAN_TICKERS[sectorKey] || [];
      for (const item of sectorTickers) {
        current++;
        setScanProgress({ current, total, sector: item.sector });
        try {
          const analysis = await algoAPI.analyze(item.ticker, apiKey);
          if (!analysis.error) {
            results.push({
              ...item,
              sectorKey,
              price: analysis.price,
              score: analysis.analysis.score,
              signal: analysis.analysis.signal,
              rsi: analysis.indicators.rsi,
              trend: analysis.analysis.trend,
              support: analysis.levels.support,
              resistance: analysis.levels.resistance,
              sparkline: analysis.sparkline,
              tradeSetup: analysis.trade_setup,
              entry_price: analysis.trade_setup?.entry || null,
            });
          }
        } catch (e) {
          console.error(`Scan error for ${item.ticker}:`, e);
        }
        await delay(180);
      }
    }
    results.sort((a, b) => b.score - a.score);
    setScanResults(results);
    setScanning(false);
    // Auto-save scan results to DB
    if (results.length > 0) {
      algoAPI.saveScanResults(results);
    }
  }, [apiKey, selectedSectors]);

  // Algo Analysis
  const runAlgoAnalysis = useCallback(async (ticker, forceRefresh = false) => {
    if (!ticker) return;
    setAlgoLoading(true);
    setAlgoAnalysis(null);
    setChartData(null);
    setChartTimeframe("3M");
    
    // Check history cache first (unless force refresh)
    if (!forceRefresh) {
      const cached = await algoAPI.getHistoryTicker(owner, ticker);
      if (cached && cached.analysis) {
        setAlgoAnalysis(cached.analysis);
        setAlgoLoading(false);
        // Fetch valuation in background if not already loaded
        if (!valuationData[ticker]) {
          finnhubAPI.getValuationBatch([ticker]).then(val => {
            if (val && Object.keys(val).length > 0) setValuationData(prev => ({ ...prev, ...val }));
          });
        }
        // Always fetch fresh chart data AND update the price
        algoAPI.getChart(ticker, "3M").then(cd => {
          if (cd && cd.closes?.length > 0) {
            setChartData(cd);
            const latestClose = cd.closes[cd.closes.length - 1];
            setAlgoAnalysis(prev => prev ? { ...prev, chart_data: cd, price: parseFloat(latestClose.toFixed(2)) } : prev);
          }
        });
        // Fetch fresh real-time price from yfinance
        finnhubAPI.getYFinanceBatchQuotes([ticker]).then(data => {
          if (data && data[ticker] && data[ticker].c > 0) {
            setAlgoAnalysis(prev => prev ? { ...prev, price: data[ticker].c } : prev);
          }
        });
        return;
      }
    }
    
    try {
      const result = await algoAPI.analyze(ticker, apiKey || "dummy");
      if (result?.error) {
        setAlgoAnalysis({ ticker, error: result.error, analysis: { score: 0, signal: "UNKNOWN" }, price: 0 });
        setAlgoLoading(false);
        return;
      }
      setAlgoAnalysis(result);
      // Save to history
      await algoAPI.saveAnalysis(owner, result);
      // Refresh history list and top rated
      const history = await algoAPI.getHistory(owner);
      setAlgoHistory(history);
      algoAPI.getTopRated(owner).then(r => setAlgoTopRated(r?.top_rated || []));
      topPicksAPI.getDaily().then(r => setDailyTopPicks(r?.top_rated || []));
      // Fetch valuation in background
      if (!valuationData[ticker]) {
        finnhubAPI.getValuationBatch([ticker]).then(val => {
          if (val && Object.keys(val).length > 0) setValuationData(prev => ({ ...prev, ...val }));
        });
      }
    } catch (e) {
      console.error("Algo analysis error:", e);
      setAlgoAnalysis({ ticker, error: "Erreur d'analyse pour ce ticker", analysis: { score: 0, signal: "UNKNOWN" }, price: 0 });
    }
    setAlgoLoading(false);
  }, [apiKey, owner, valuationData]);

  // Fetch chart data for different timeframes
  const fetchChartData = useCallback(async (ticker, tf) => {
    if (!ticker) return;
    setChartLoading(true);
    try {
      const data = await algoAPI.getChart(ticker, tf);
      if (data) setChartData(data);
    } catch (e) {
      console.error("Chart fetch error:", e);
    }
    setChartLoading(false);
  }, []);

  // When timeframe changes or ticker changes, fetch new chart data
  useEffect(() => {
    if (!algoAnalysis?.ticker) return;
    if (chartTimeframe === "3M" && algoAnalysis?.chart_data?.closes?.length > 0) {
      // Use embedded chart_data only if it matches current ticker
      setChartData(algoAnalysis.chart_data);
    } else {
      fetchChartData(algoAnalysis.ticker, chartTimeframe);
    }
  }, [chartTimeframe, algoAnalysis?.ticker, fetchChartData]);

  // Fetch stock performance + fundamentals by period
  useEffect(() => {
    if (!algoAnalysis?.ticker) return;
    const t = algoAnalysis.ticker;
    // Fetch performance
    if (stockPerf._ticker !== t) {
      finnhubAPI.getStockPerformance(t).then(p => {
        if (p && Object.keys(p).length > 0) setStockPerf({ ...p, _ticker: t });
      });
    }
    // Always fetch fundamentals independently
    if (stockFundamentals?._ticker !== t) {
      finnhubAPI.getStockFundamentals(t).then(data => {
        if (data?.kpis) setStockFundamentals({ ...data, _ticker: t });
      });
    }
    if (fundamentalsPro?._ticker !== t) {
      finnhubAPI.getFundamentalsPro(t).then(data => {
        if (data && !data.error) setFundamentalsPro({ ...data, _ticker: t });
      });
    }
    setBacktestConclusion("");
  }, [algoAnalysis?.ticker]);

  // Backtest
  const runBacktest = useCallback(async () => {
    if (!algoTicker) return;
    setAlgoLoading(true);
    try {
      const result = await algoAPI.backtest(algoTicker, apiKey, backtestStrategy, 10000);
      setBacktestResult(result);
    } catch (e) {
      console.error("Backtest error:", e);
    }
    setAlgoLoading(false);
  }, [apiKey, algoTicker, backtestStrategy]);

  // Portfolio Optimization
  const runOptimization = useCallback(async () => {
    setAlgoLoading(true);
    try {
      const currentPositions = dbPortfolio.length > 0 ? dbPortfolio : PORTFOLIO;
      const positions = currentPositions.filter(p => p.owner === owner);
      const result = await algoAPI.optimize(positions, prices, "BALANCED");
      setPortfolioOptimization(result);
    } catch (e) {
      console.error("Optimization error:", e);
    }
    setAlgoLoading(false);
  }, [owner, prices, dbPortfolio]);

  // AI Analysis
  const askAI = useCallback(async (prompt, includeScanResults = false, extraContext = "") => {
    setAiLoading(true);
    setExpertResults(null);
    const currentPositions = dbPortfolio.length > 0 ? dbPortfolio : PORTFOLIO;
    const ownerPositions = currentPositions.filter(p => p.owner === owner);
    
    // Build RICH portfolio context with all data
    let totalValue = 0;
    let totalCost = 0;
    const posDetails = ownerPositions.map(p => {
      const pr = prices[p.ticker] || p.avg;
      const value = pr * p.qty;
      const cost = p.avg * p.qty;
      const pnl = value - cost;
      const pnlPct = ((pr - p.avg) / p.avg * 100).toFixed(1);
      const sr = supportResistance[p.ticker];
      totalValue += value;
      totalCost += cost;
      
      let line = `${p.ticker} (${p.name}) | Stratégie: ${(p.strategy || "LT") === "LT" ? "LONG TERM" : "SWING (max 1 mois)"} | Secteur: ${p.sector || "N/A"} | Qty: ${p.qty} | Entry: ${getTickerCurrency(p.ticker)}${p.avg} | Current: ${getTickerCurrency(p.ticker)}${typeof pr === "number" ? pr.toFixed(2) : pr} | P&L: ${pnl >= 0 ? "+" : ""}${getTickerCurrency(p.ticker)}${Math.round(pnl)} (${pnlPct}%) | Valeur: ${getTickerCurrency(p.ticker)}${Math.round(value).toLocaleString()}`;
      if (sr) {
        line += ` | Support: ${getTickerCurrency(p.ticker)}${sr.support} | Résistance: ${getTickerCurrency(p.ticker)}${sr.resistance}`;
        if (pr <= sr.support * 1.02) line += " [PROCHE DU SUPPORT - ATTENTION]";
        if (pr >= sr.resistance * 0.98) line += " [PROCHE DE LA RÉSISTANCE]";
      }
      return line;
    }).join("\n");
    
    const totalPnl = totalValue - totalCost;
    const totalPnlPct = totalCost ? ((totalPnl / totalCost) * 100).toFixed(1) : "0";
    
    // Find best/worst performers
    const sorted = ownerPositions.map(p => {
      const pr = prices[p.ticker] || p.avg;
      return { ticker: p.ticker, pnlPct: ((pr - p.avg) / p.avg * 100) };
    }).sort((a, b) => b.pnlPct - a.pnlPct);
    
    const best = sorted[0];
    const worst = sorted[sorted.length - 1];
    
    const ltPositions = ownerPositions.filter(p => (p.strategy || "LT") === "LT");
    const stPositions = ownerPositions.filter(p => (p.strategy || "LT") === "ST");
    
    const context = `PORTEFEUILLE DE ${owner.toUpperCase()} — DONNÉES TEMPS RÉEL
═══════════════════════════════════
PROFIL: Investisseur dual-stratégie | ${ltPositions.length} positions LONG TERM + ${stPositions.length} positions SWING (max 1 mois)
Valeur totale: $${Math.round(totalValue).toLocaleString()} | Coût total: $${Math.round(totalCost).toLocaleString()}
P&L Total: ${totalPnl >= 0 ? "+" : ""}$${Math.round(totalPnl).toLocaleString()} (${totalPnlPct}%)
Meilleur: ${best?.ticker || "N/A"} (${best ? (best.pnlPct >= 0 ? "+" : "") + best.pnlPct.toFixed(1) + "%" : ""})
Pire: ${worst?.ticker || "N/A"} (${worst ? (worst.pnlPct >= 0 ? "+" : "") + worst.pnlPct.toFixed(1) + "%" : ""})
Nombre de positions: ${ownerPositions.length}
═══════════════════════════════════
IMPORTANT: Adapter vos recommandations selon la stratégie de chaque position:
- LONG TERM: Focus fondamentaux, thèse long terme, niveaux d'accumulation
- SWING: Focus technique, momentum, stop-loss serrés, objectifs court terme (max 1 mois)
═══════════════════════════════════
DÉTAIL DES POSITIONS:
${posDetails}`;
    
    let scanContext = "";
    if (includeScanResults && scanResults.length > 0) {
      scanContext = "\n\nMARKET SCAN (Top 10):\n" + scanResults.slice(0, 10).map(r => 
        `${r.ticker}: $${r.price.toFixed(2)} | Score: ${r.score} | Signal: ${r.signal}`
      ).join("\n");
    }
    
    // Add NEWS context
    let newsContext = "";
    if (marketNews.length > 0) {
      newsContext = "\n\nDERNIÈRES NEWS MARCHÉS (temps réel):\n" + marketNews.slice(0, 8).map(n => 
        `- [${n.source || ""}] ${n.headline || ""}`
      ).join("\n");
    }
    
    // Add INSIDER TRADING context
    let insiderContext = "";
    if (insiderData.length > 0) {
      const insiderLines = [];
      insiderData.forEach(item => {
        if (item.transactions && item.transactions.length > 0) {
          const buys = item.transactions.filter(t => t.change > 0);
          const sells = item.transactions.filter(t => t.change < 0);
          if (buys.length > 0 || sells.length > 0) {
            insiderLines.push(`${item.ticker}: ${buys.length > 0 ? `${buys.length} ACHATS insiders (${buys.map(b => b.name).join(", ")})` : ""}${sells.length > 0 ? ` ${sells.length} VENTES insiders (${sells.map(s => s.name).join(", ")})` : ""}`);
          }
        }
      });
      if (insiderLines.length > 0) {
        insiderContext = "\n\nINSIDER TRADING (activité récente des dirigeants):\n" + insiderLines.join("\n");
      }
    }
    
    const fullContext = context + scanContext + newsContext + insiderContext + extraContext;
    
    // Add user message to history
    setChatHistory(prev => [...prev, { role: "user", content: prompt }]);
    setLastPrompt(prompt);
    setLastContext(fullContext);
    setDebateResults(null);
    setSynthesisResult(null);
    setFollowupMessages([]);
    setFollowupInput("");
    
    try {
      const imagePayload = chatImages.map(img => ({ content_type: img.content_type, base64: img.base64 }));
      const response = await aiAPI.expertChat(prompt, fullContext, `apex-expert-${Date.now()}`, imagePayload);
      setExpertResults(response);
      setChatHistory(prev => [...prev, { role: "experts", data: response }]);
    } catch (e) {
      setExpertResults({ error: "Erreur de connexion avec les experts." });
    }
    setChatImages([]);
    setAiLoading(false);
  }, [prices, owner, getPrice, scanResults, dbPortfolio, supportResistance, marketNews, insiderData, chatImages]);

  // === FULL PORTFOLIO REVIEW ===
  const runFullReview = useCallback(async () => {
    // 1. Fetch all latest data in parallel
    const [topMovesRes, topPicksRes, capitalFlowRes, marketTimingRes] = await Promise.allSettled([
      institutionalAPI.getTopMoves(),
      topPicksAPI.getDaily(),
      capitalFlowAPI.getData(),
      watchlistAPI.getMarketTiming(),
    ]);

    const topMoves = topMovesRes.status === "fulfilled" ? topMovesRes.value : {};
    const topPicks = topPicksRes.status === "fulfilled" ? topPicksRes.value : {};
    const capitalFlow = capitalFlowRes.status === "fulfilled" ? capitalFlowRes.value : {};
    const mtData = marketTimingRes.status === "fulfilled" ? marketTimingRes.value : {};

    // 2. Build mega-context
    let extraContext = "\n\n═══════════════════════════════════\nDONNÉES MARCHÉ COMPLÈTES (TEMPS RÉEL)\n═══════════════════════════════════\n";

    // Portfolio positions (computed locally to avoid hoisting issues)
    const currentPos = dbPortfolio.length > 0 ? dbPortfolio : PORTFOLIO;
    const reviewPositions = currentPos.filter(p => p.owner === owner);
    if (reviewPositions.length > 0) {
      const tv = reviewPositions.reduce((s, p) => s + getPrice(p) * p.qty, 0);
      const tc = reviewPositions.reduce((s, p) => s + p.avg * p.qty, 0);
      const rpnl = tv - tc;
      const rpct = tc > 0 ? (rpnl / tc * 100) : 0;
      extraContext += `\nPORTEFEUILLE ${owner.toUpperCase()} (${reviewPositions.length} positions):\n`;
      extraContext += `Valeur totale: ~${tv.toFixed(0)}€ | P&L: ${rpnl >= 0 ? "+" : ""}${rpnl.toFixed(0)}€ (${rpct.toFixed(1)}%)\n`;
      reviewPositions.forEach(p => {
        const pr = getPrice(p);
        const ppct = ((pr - p.avg) / p.avg * 100).toFixed(1);
        const val = (pr * p.qty).toFixed(0);
        const dc = dailyChanges[p.ticker];
        const dayPct = dc?.dp != null ? `${dc.dp >= 0 ? "+" : ""}${dc.dp.toFixed(1)}%` : "";
        extraContext += `  ${p.ticker}: ${p.qty} x ${p.avg.toFixed(2)} → ${pr.toFixed(2)} | P&L=${ppct}% | Val=${val}€ | Jour=${dayPct} | Strat=${p.strategy || "LT"}\n`;
      });
    }

    // Market Timing
    if (mtData.signal) {
      extraContext += `\nMARKET TIMING: Signal=${mtData.signal} Score=${mtData.score}/100 VIX=${mtData.vix || "?"}\n`;
      extraContext += `Indices: ${(mtData.indices || []).map(i => `${i.name}: ${i.trend}`).join(", ")}\n`;
    }

    // Top rated from daily scan
    if (topPicks.top_rated?.length > 0) {
      extraContext += `\nTOP PICKS DU SCAN QUOTIDIEN (${topPicks.top_rated.length} actions):\n`;
      topPicks.top_rated.slice(0, 15).forEach(p => {
        extraContext += `  ${p.ticker}: $${p.price} | Score=${p.composite} | Conv=${p.conviction} | Trade=${p.trade_type} | Mom1m=${p.momentum_1m}% | R/R=${p.risk_reward} | Invest=${p.recommended_investment}€\n`;
      });
    }

    // Swing picks
    if (swingPicks.length > 0) {
      extraContext += `\nSWING DETECTOR (Règle: No Break No Trade):\n`;
      swingPicks.forEach(s => {
        extraContext += `  ${s.ticker}: Score=${s.swing_score}/100 | Action=${s.swing_action} | Setup=${s.swing_setup} | RSI=${s.rsi} | Vol=${s.vol_ratio}x | Prix=$${s.price} | Risque=${s.swing_risk}\n`;
      });
    }

    // Institutional moves
    if (topMoves.top_moves?.length > 0) {
      extraContext += `\nMOUVEMENTS INSTITUTIONNELS (13F):\n`;
      topMoves.top_moves.forEach(m => {
        extraContext += `  ${m.ticker}: ${m.fund} | $${m.value_millions}M | Score=${m.interest_score} | Prix=$${m.current_price || "?"} | ${(m.reasons || []).join(", ")}\n`;
      });
    }

    // Capital Flow
    if (capitalFlow.unusual_volume?.length > 0) {
      extraContext += `\nCAPITAL FLOW - VOLUME INHABITUEL:\n`;
      capitalFlow.unusual_volume.slice(0, 10).forEach(v => {
        extraContext += `  ${v.ticker}: Vol=${v.vol_ratio}x | Prix=$${v.price} | Change=${v.change}%\n`;
      });
    }
    if (capitalFlow.options_flow?.length > 0) {
      extraContext += `\nCAPITAL FLOW - OPTIONS FLOW (SMART MONEY):\n`;
      capitalFlow.options_flow.slice(0, 10).forEach(o => {
        extraContext += `  ${o.ticker}: ${o.type} | ${o.description}\n`;
      });
    }

    // Scanner top results
    if (scanResults.length > 0) {
      extraContext += `\nSCANNER MASSIF (Top 20 / ${scanResults.length} actions):\n`;
      scanResults.slice(0, 20).forEach(r => {
        extraContext += `  ${r.ticker}: $${r.price?.toFixed(2)} | Score=${r.buy_score} | Signal=${r.signal} | Trend=${r.trend} | Vol=${r.vol_ratio}x\n`;
      });
    }

    const fullReviewPrompt = `🔍 REVUE COMPLÈTE — ANALYSE QUANTITATIVE & TRADING

Agis comme un analyste quantitatif et trader professionnel.

CONTRAINTES ABSOLUES:
- Ne jamais spéculer sans données vérifiables
- Prioriser les données de marché (prix, volume, tendance) fournies ci-dessous
- Ignorer les opinions médiatiques ou narratives non confirmées
- Si une information est incertaine, indiquer "⚠️ information incertaine"
- Ne proposer que des trades avec un ratio minimum de 2:1
- Prioriser les configurations claires (breakout, pullback, continuation)
- Éviter les actifs illiquides ou manipulés

ANALYSE DEMANDÉE (FORMAT STRUCTURÉ):

1. **DIAGNOSTIC MARCHÉ** — Tendance globale (haussière/baissière/range), supports/résistances majeurs, volatilité, et catalyseurs réels identifiés (résultats financiers, annonces macro, décisions politiques).

2. **DIAGNOSTIC PORTEFEUILLE** — Analyse critique de mes positions actuelles. Forces, faiblesses, corrélations dangereuses. Score global /100.

3. **POSITIONS À VENDRE** — Quelles positions couper/réduire et POURQUOI (momentum cassé, fondamentaux dégradés, invalidation technique). Pour chaque:
   - Prix actuel (vérifié dans les données)
   - Niveau d'invalidation atteint ou proche
   - Suggestion de sortie

4. **TOP 5 SWING TRADES (No Break No Trade)** — 5 meilleures opportunités swing (2-10 jours) parmi les données scannées. UNIQUEMENT si breakout confirmé ou pullback sur support validé. Pour chaque:
   - Ticker + Prix actuel
   - Zone d'entrée optimale
   - Stop loss (niveau d'invalidation)
   - Take profit (objectif réaliste)
   - Ratio risque/rendement
   - **SCORE /100** basé sur: force du momentum (40pts) + qualité du catalyseur (30pts) + confirmation technique (30pts)

5. **TOP 5 INVESTISSEMENT LONG TERME** — 5 actions à accumuler. Thèse fondamentale, prix d'entrée idéal, allocation recommandée (base 100k€).
   - **SCORE /100** pour chaque

6. **REMPLACEMENTS CONCRETS** — Pour chaque position à vendre, le remplacement exact (ticker + pourquoi c'est mieux).

7. **PLAN D'ACTION SEMAINE** — Résumé en bullet points: quoi faire, dans quel ordre, avec montants précis.

STYLE: Structuré, clair, sans émotion. Basé uniquement sur les données fournies. Orienté action.`;

    // Use askAI with the full review prompt and extra context injected
    askAI(fullReviewPrompt, true, extraContext);
  }, [swingPicks, scanResults, prices, owner, askAI, dbPortfolio, dailyChanges, getPrice]);

  const startDebate = useCallback(async () => {
    if (!expertResults?.experts) return;
    setDebateLoading(true);
    try {
      const response = await aiAPI.expertDebate(lastPrompt, expertResults.experts, lastContext, `apex-debate-${Date.now()}`);
      setDebateResults(response);
    } catch (e) {
      setDebateResults({ error: "Erreur lors du débat." });
    }
    setDebateLoading(false);
  }, [expertResults, lastPrompt, lastContext]);

  const startSynthesis = useCallback(async () => {
    if (!expertResults?.experts) return;
    setSynthesisLoading(true);
    try {
      const response = await aiAPI.expertSynthesis(
        lastPrompt,
        expertResults.experts,
        expertResults.experts,
        expertResults.consensus || "",
        expertResults.consensus || "",
        lastContext
      );
      setSynthesisResult(response);
      // Auto-save conversation after synthesis
      aiAPI.saveConversation(owner, lastPrompt, expertResults, null, response, []);
    } catch (e) {
      setSynthesisResult({ error: "Erreur lors de la synthèse." });
    }
    setSynthesisLoading(false);
  }, [expertResults, lastPrompt, lastContext, owner]);

  const sendFollowup = useCallback(async () => {
    if (!followupInput.trim() || followupLoading) return;
    const question = followupInput.trim();
    setFollowupInput("");
    setFollowupLoading(true);
    const newMessages = [...followupMessages, { role: "user", content: question, timestamp: new Date().toISOString() }];
    setFollowupMessages(newMessages);
    try {
      const response = await aiAPI.expertFollowup(
        question, expertResults, null, synthesisResult, followupMessages, lastContext, lastPrompt
      );
      const updatedMessages = [...newMessages, { role: "expert", content: response.response, timestamp: new Date().toISOString() }];
      setFollowupMessages(updatedMessages);
      // Auto-save with followup
      aiAPI.saveConversation(owner, lastPrompt, expertResults, null, synthesisResult, updatedMessages);
    } catch (e) {
      setFollowupMessages([...newMessages, { role: "expert", content: "Erreur de connexion avec le comité.", timestamp: new Date().toISOString() }]);
    }
    setFollowupLoading(false);
  }, [followupInput, followupLoading, followupMessages, expertResults, debateResults, synthesisResult, lastContext, lastPrompt, owner]);

  const loadConversation = useCallback((conv) => {
    setExpertResults(conv.round1 || null);
    setDebateResults(conv.round2 || null);
    setSynthesisResult(conv.round3 || null);
    setFollowupMessages(conv.followup_messages || []);
    setLastPrompt(conv.prompt || "");
    setShowHistory(false);
  }, []);

  const loadConversationHistory = useCallback(async () => {
    const convos = await aiAPI.getConversations(owner, 20);
    setConversationHistory(convos);
    setShowHistory(true);
  }, [owner]);

  const analyzePosition = useCallback(async (pos) => {
    setSelectedPosition(pos);
    setAlgoTicker(pos.ticker);
    setTab("algo");
    runAlgoAnalysis(pos.ticker);
  }, [runAlgoAnalysis]);

  const connect = useCallback(async (key) => {
    setApiKey(key);
    setLoading(true);
    
    // Load portfolio FIRST so strategy data is available before UI renders
    const freshPositions = await loadPortfolio();
    
    // Fetch quotes + market status + exchange rates IN PARALLEL (big speed win)
    const [, , ] = await Promise.allSettled([
      fetchQuotes(key, freshPositions),
      marketAPI.getStatus().then(status => {
        if (status && Object.keys(status).length > 0) setMarketStatus(status);
      }).catch(() => {}),
      marketAPI.getExchangeRates().then(fxData => {
        if (fxData?.rates) setExchangeRates(fxData.rates);
      }).catch(() => {}),
    ]);
    
    setLoading(false);
    
    // Non-critical data: load in background AFTER UI is interactive
    fetchCandles(key, freshPositions).catch(() => {});
    // Fetch pre/post market data in background
    const pmTickers = [...new Set([...freshPositions.map(p => p.ticker)])].filter(t => !["BTC","ETH","SOL","XRP"].includes(t)).slice(0, 20);
    finnhubAPI.getPrePostMarketBatch(pmTickers).then(pmData => {
      if (pmData && Object.keys(pmData).length > 0) setPostMarketData(prev => ({ ...prev, ...pmData }));
    }).catch(() => {});
    // News & Insider: removed from auto-load (load on demand only)
    
    // Auto-refresh quotes every 30s
    intervalRef.current = setInterval(() => fetchQuotes(key), 30000);
    
    // Refresh market status every 5 minutes
    const marketInterval = setInterval(async () => {
      try {
        const status = await marketAPI.getStatus();
        if (status) setMarketStatus(status);
      } catch (e) {}
    }, 300000);
    
    return () => {
      clearInterval(intervalRef.current);
      clearInterval(marketInterval);
    };
  }, [fetchQuotes, fetchCandles, fetchNews, loadPortfolio]);

  // Auto-login from localStorage
  useEffect(() => {
    const token = localStorage.getItem("apex_token");
    const savedUser = localStorage.getItem("apex_user");
    if (token && savedUser) {
      try {
        const u = JSON.parse(savedUser);
        setUser(u);
        setOwner(u.name || "Helmi");
        setApiKey(u.finnhub_key || "");
        setSettingsKey(u.finnhub_key || "");
        if (u.finnhub_key) {
          connect(u.finnhub_key);
        } else {
          connect("");
        }
      } catch (e) {
        localStorage.removeItem("apex_token");
        localStorage.removeItem("apex_user");
      }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAuth = useCallback((userData) => {
    setUser(userData);
    setOwner(userData.name || "Helmi");
    setApiKey(userData.finnhub_key || "");
    setSettingsKey(userData.finnhub_key || "");
    if (userData.finnhub_key) {
      connect(userData.finnhub_key);
    } else {
      connect("");
    }
  }, [connect]);

  // Load essential Portfolio data when user is available (DELAYED to avoid request flooding)
  useEffect(() => {
    if (user && owner) {
      const timer = setTimeout(() => {
        portfolioAPI.getSellSignals(owner).then(r => setSellSignals(r?.signals || {}));
        algoAPI.getTopRated(owner).then(r => setAlgoTopRated(r?.top_rated || []));
        loadRealizedPnl();
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [user, owner, loadRealizedPnl]);

  // Load Top 10 du Jour independently (DELAYED)
  useEffect(() => {
    const loadTopPicks = () => {
      topPicksAPI.getDaily().then(r => {
        const picks = r?.top_rated || [];
        if (picks.length > 0) setDailyTopPicks(picks);
      }).catch(() => {});
    };
    const timer = setTimeout(loadTopPicks, 4000);
    const interval = setInterval(loadTopPicks, 5 * 60 * 1000);
    return () => { clearTimeout(timer); clearInterval(interval); };
  }, []);

  // Load Pullback Opportunities (DELAYED)
  useEffect(() => {
    const loadPullbacks = () => {
      setPullbackLoading(true);
      marketAPI.getOpportunities().then(r => {
        setPullbackOpps(r?.opportunities || []);
      }).catch(() => {}).finally(() => setPullbackLoading(false));
    };
    const timer = setTimeout(loadPullbacks, 5000);
    const interval = setInterval(loadPullbacks, 15 * 60 * 1000);
    return () => { clearTimeout(timer); clearInterval(interval); };
  }, []);


  const handleLogout = useCallback(() => {
    localStorage.removeItem("apex_token");
    localStorage.removeItem("apex_user");
    setUser(null);
    setApiKey("");
    if (intervalRef.current) clearInterval(intervalRef.current);
  }, []);

  // Portfolio command handler
  const handlePortfolioCommand = useCallback(async () => {
    if (!portfolioCmd || portfolioCmdLoading) return;
    setPortfolioCmdLoading(true);
    try {
      const res = await portfolioAPI.command(portfolioCmd, owner);
      setPortfolioCmdResult(res);
      if (res.status === "success") {
        setPortfolioCmd("");
        const fresh = await portfolioAPI.getPositions(owner);
        setDbPortfolio(fresh);
        toast.success(res.message);
      } else {
        toast.error(res.message || "Commande non comprise");
      }
    } catch (err) {
      toast.error("Erreur d'exécution");
    }
    setPortfolioCmdLoading(false);
  }, [portfolioCmd, portfolioCmdLoading, owner]);

  // Trigger massive scan (backend runs it in bg, we poll for fresh results)
  const triggerMassiveScan = useCallback(async () => {
    setMassiveScan(prev => ({ ...prev, loading: true }));
    try {
      const res = await massiveScanAPI.triggerScan();
      if (res.ok || res.status === "started") {
        // Poll every 15s for up to 5min for scan to finish (backend runs in bg)
        const startTime = new Date(res.started_at || new Date().toISOString());
        for (let i = 0; i < 20; i++) {
          await new Promise(r => setTimeout(r, 15000));
          const data = await massiveScanAPI.getResults();
          if (data.scanned_at && new Date(data.scanned_at) > startTime) {
            setMassiveScan({ results: data.results || [], loading: false, scannedAt: data.scanned_at });
            return;
          }
        }
        // Fallback: return whatever we have after 5min
        const data = await massiveScanAPI.getResults();
        setMassiveScan({ results: data.results || [], loading: false, scannedAt: data.scanned_at });
      } else {
        setMassiveScan(prev => ({ ...prev, loading: false }));
      }
    } catch (e) {
      setMassiveScan(prev => ({ ...prev, loading: false }));
    }
  }, []);

  const handleSaveSettings = useCallback(async () => {
    try {
      await authAPI.updateProfile({ finnhub_key: settingsKey });
      const updatedUser = { ...user, finnhub_key: settingsKey };
      setUser(updatedUser);
      localStorage.setItem("apex_user", JSON.stringify(updatedUser));
      setApiKey(settingsKey);
      setShowSettings(false);
      if (settingsKey) connect(settingsKey);
    } catch (e) {
      console.error("Error saving settings:", e);
    }
  }, [settingsKey, user, connect]);

  // Watchlist functions
  const loadWatchlist = useCallback(async () => {
    const data = await watchlistAPI.get(owner);
    if (data && data.stocks) {
      setWatchlist(data.stocks);
      setWatchlistGeneratedAt(data.generated_at || data.updated_at);
    }
  }, [owner]);

  const generateWatchlist = useCallback(async () => {
    setWatchlistGenerating(true);
    try {
      const data = await watchlistAPI.generate(owner, apiKey);
      if (data && data.stocks) {
        setWatchlist(data.stocks);
        setWatchlistGeneratedAt(data.generated_at);
      }
    } catch (e) {
      console.error("Watchlist generation error:", e);
    }
    setWatchlistGenerating(false);
  }, [owner, apiKey]);

  const addToWatchlist = useCallback(async (ticker, name, sector, region) => {
    try {
      toast.info(`Recherche de ${ticker} sur toutes les bourses...`);
      const result = await watchlistAPI.addStock(owner, ticker, name, sector, region);
      if (result.resolved_ticker && result.resolved_ticker !== ticker) {
        toast.success(`${ticker} trouvé comme ${result.resolved_ticker} (${result.resolved_region})`);
      } else {
        toast.success(`${ticker} ajouté à la watchlist`);
      }
      await loadWatchlist();
    } catch (e) {
      const msg = e.response?.data?.detail || "Ticker introuvable";
      toast.error(msg);
      console.error("Add to watchlist error:", e);
    }
  }, [owner, loadWatchlist]);

  const removeFromWatchlist = useCallback(async (ticker) => {
    try {
      await watchlistAPI.removeStock(owner, ticker);
      setWatchlist(prev => prev.filter(s => s.ticker !== ticker));
    } catch (e) {
      console.error("Remove from watchlist error:", e);
    }
  }, [owner]);

  // Load watchlist on mount (DELAYED)
  useEffect(() => {
    if (owner) {
      const timer = setTimeout(loadWatchlist, 2000);
      return () => clearTimeout(timer);
    }
  }, [owner, loadWatchlist]);

  // Play notification sound using Web Audio API
  const playBuyZoneSound = useCallback(() => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = "sine";
      // Two-tone "ding ding"
      osc.frequency.setValueAtTime(880, ctx.currentTime);
      osc.frequency.setValueAtTime(1100, ctx.currentTime + 0.15);
      gain.gain.setValueAtTime(0.3, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.4);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.4);
    } catch (e) { /* Audio not available */ }
  }, []);

  // Request browser notification permission
  useEffect(() => {
    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission();
    }
  }, []);

  // Fetch live prices for watchlist stocks + detect buy zone transitions
  const refreshWatchlistPrices = useCallback(async () => {
    if (watchlist.length === 0) return;
    const tickers = watchlist.map(s => s.ticker);
    try {
      const data = await finnhubAPI.getYFinanceBatchQuotes(tickers);
      if (data && Object.keys(data).length > 0) {
        const priceMap = {};
        const fullQuoteMap = {};
        Object.entries(data).forEach(([t, q]) => {
          if (q && q.c > 0) {
            priceMap[t] = q.c;
            fullQuoteMap[t] = q;
          }
        });

        // 🔧 2026-05-22: persist buy-zone state to localStorage so it survives tab
        // navigation/remount. Without this, every time the user came back to the
        // app every in-zone stock re-fired a toast → spam.
        // Also: per-ticker cooldown 6h so a stock that stays in zone doesn't re-spam.
        const STORAGE_KEY = `mtrades_buyzone_${owner || "anon"}`;
        const COOLDOWN_KEY = `mtrades_buyzone_cooldown_${owner || "anon"}`;
        const COOLDOWN_MS = 6 * 60 * 60 * 1000;
        let prevZones = prevBuyZoneRef.current;
        if (!prevZones || Object.keys(prevZones).length === 0) {
          try {
            const raw = localStorage.getItem(STORAGE_KEY);
            if (raw) prevZones = JSON.parse(raw);
          } catch (_) { prevZones = {}; }
        }
        let cooldowns = {};
        try {
          const raw = localStorage.getItem(COOLDOWN_KEY);
          if (raw) cooldowns = JSON.parse(raw);
        } catch (_) {}
        const nowMs = Date.now();

        const newAlerts = [];
        watchlist.forEach(stock => {
          const livePrice = priceMap[stock.ticker] || stock.price;
          const entryPrice = stock.entry_price || 0;
          if (entryPrice <= 0) return;
          const nowInBuyZone = livePrice <= entryPrice;
          const wasInBuyZone = prevZones[stock.ticker] || false;
          const lastAlerted = cooldowns[stock.ticker] || 0;
          const cooldownExpired = (nowMs - lastAlerted) > COOLDOWN_MS;
          // Only fire if: just entered the zone OR cooldown expired AND still in zone
          if (nowInBuyZone && !wasInBuyZone && cooldownExpired) {
            newAlerts.push(stock);
            cooldowns[stock.ticker] = nowMs;
          }
        });

        // Update previous zone state + persist to localStorage
        const newZoneState = {};
        watchlist.forEach(stock => {
          const livePrice = priceMap[stock.ticker] || stock.price;
          newZoneState[stock.ticker] = stock.entry_price > 0 && livePrice <= stock.entry_price;
        });
        prevBuyZoneRef.current = newZoneState;
        try {
          localStorage.setItem(STORAGE_KEY, JSON.stringify(newZoneState));
          localStorage.setItem(COOLDOWN_KEY, JSON.stringify(cooldowns));
        } catch (_) {}

        // Fire notifications for new buy zone entries
        if (newAlerts.length > 0) {
          playBuyZoneSound();
          setWatchlistAlertCount(prev => prev + newAlerts.length);
          newAlerts.forEach(stock => {
            const livePrice = priceMap[stock.ticker] || stock.price;
            toast.success(
              `ZONE D'ACHAT : ${stock.name}`,
              {
                description: `${stock.ticker} a atteint ${formatTickerPrice(livePrice, stock.ticker)} (entrée: ${formatTickerPrice(stock.entry_price, stock.ticker)})`,
                duration: 10000,
                action: {
                  label: "Analyser",
                  onClick: () => { setAlgoTicker(stock.ticker); setTab("algo"); runAlgoAnalysis(stock.ticker); }
                }
              }
            );
          });
        }

        setWatchlistLivePrices(fullQuoteMap);
      }
    } catch (e) {
      console.error("Watchlist price refresh error:", e);
    }
  }, [watchlist, playBuyZoneSound, setAlgoTicker, setTab, runAlgoAnalysis, owner]);

  // Auto-refresh watchlist prices every 5 min + delayed first load
  // 🔧 2026-05-22: 2 min → 5 min to reduce toast spam and yfinance API load.
  useEffect(() => {
    if (watchlist.length > 0) {
      const timer = setTimeout(refreshWatchlistPrices, 6000);
      const interval = setInterval(refreshWatchlistPrices, 300000);
      return () => { clearTimeout(timer); clearInterval(interval); };
    }
  }, [watchlist.length, refreshWatchlistPrices]);

  // Fetch watchlist extra data only when entering watchlist tab (not at login)
  useEffect(() => {
    if (tab === "watchlist" && watchlist.length > 0) {
      const wlTickers = watchlist.map(s => s.ticker).filter(t => !t.includes("-USD") && !t.includes("=F"));
      const missingVal = wlTickers.filter(t => !valuationData[t]);
      if (missingVal.length > 0) {
        const batchSize = 5;
        const batches = [];
        for (let i = 0; i < missingVal.length; i += batchSize) {
          batches.push(missingVal.slice(i, i + batchSize));
        }
        batches.forEach((batch, idx) => {
          setTimeout(() => {
            finnhubAPI.getValuationBatch(batch).then(val => {
              if (val && Object.keys(val).length > 0) setValuationData(prev => ({ ...prev, ...val }));
            });
          }, idx * 3000);
        });
      }
      const missingAth = wlTickers.filter(t => !athData[t]);
      if (missingAth.length > 0) {
        finnhubAPI.getATHBatch(missingAth).then(ath => {
          if (ath && Object.keys(ath).length > 0) setAthData(prev => ({ ...prev, ...ath }));
        });
      }
    }
  }, [tab, watchlist]);

  // Clear alert count when entering watchlist tab
  useEffect(() => {
    if (tab === "watchlist") setWatchlistAlertCount(0);
  }, [tab]);

  useEffect(() => () => { if (intervalRef.current) clearInterval(intervalRef.current); }, []);

  // 🆕 Auto-load portfolio on mount (independent of API key) so KPIs show even without Finnhub
  // Live quotes still require API key — without one we fallback to avg price for valuation.
  // Also reloads when the app becomes visible again (user returns from background),
  // and every 90s while the tab is active — fixes the "needs hard refresh" UX bug.
  useEffect(() => {
    const refreshAll = () => {
      loadPortfolio().catch(e => console.error("auto-load portfolio err", e));
      marketAPI.getExchangeRates().then(fxData => {
        if (fxData?.rates) setExchangeRates(fxData.rates);
      }).catch(() => {});
    };

    refreshAll();

    // Refresh when tab/app becomes visible again (PWA returning from background)
    const onVisible = () => {
      if (document.visibilityState === "visible") refreshAll();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", refreshAll);

    // Periodic refresh every 90s while tab is active
    const interval = setInterval(() => {
      if (document.visibilityState === "visible") refreshAll();
    }, 90000);

    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", refreshAll);
      clearInterval(interval);
    };
  }, [loadPortfolio]);
  
  // Fetch ATH, earnings, valuation, and support/resistance data (deferred 3s after quotes)
  useEffect(() => {
    if (dbPortfolio.length > 0) {
      const deferTimer = setTimeout(() => {
        const allTickers = [...new Set([...dbPortfolio.map(p => p.ticker)])];
        finnhubAPI.getATHBatch(allTickers).then(ath => {
          if (ath && Object.keys(ath).length > 0) setAthData(ath);
        });
        const stockTickers = allTickers.filter(t => !t.includes("-USD") && !t.includes("=F") && !t.includes("!"));
        setTimeout(() => {
          finnhubAPI.getEarningsBatch(stockTickers).then(earn => {
            if (earn && Object.keys(earn).length > 0) setEarningsData(earn);
          });
        }, 2000);
        setTimeout(() => {
          finnhubAPI.getValuationBatch(allTickers.filter(t => !t.includes("=F") && !t.includes("!"))).then(val => {
            if (val && Object.keys(val).length > 0) setValuationData(val);
          });
        }, 4000);
        // Support/Resistance from scan data
        setTimeout(() => {
          finnhubAPI.getSupportResistanceBatch(stockTickers).then(sr => {
            if (sr && Object.keys(sr).length > 0) setSupportResistance(sr);
          });
        }, 6000);
        // Conviction scores (fundamental analysis)
        setTimeout(() => {
          convictionAPI.getScores(owner).then(data => {
            if (data?.scores) setConvictionScores(data.scores);
          }).catch(() => {});
        }, 8000);
      }, 3000);
      return () => clearTimeout(deferTimer);
    }
  }, [dbPortfolio]);

  // 💰 COST SAVING: when switching to Anne-Laure, if current tab is not allowed for
  // her, snap back to portfolio (avoids loading expensive LLM tabs).
  useEffect(() => {
    if (owner === "Anne-Laure" && !["portfolio", "watchlist", "earnings-radar", "performance"].includes(tab)) {
      setTab("portfolio");
    }
  }, [owner, tab]);

  // Load algo history when switching to algo tab
  useEffect(() => {
    if (tab === "algo" && algoHistory.length === 0) {
      algoAPI.getHistory(owner).then(h => setAlgoHistory(h || []));
    }
    if (tab === "algo") {
      algoAPI.getTopRated(owner).then(r => setAlgoTopRated(r?.top_rated || []));
      // Auto-run portfolio optimization if not already loaded or stale
      if (!portfolioOptimization && apiKey) {
        runOptimization();
      }
    }
  }, [tab, owner, algoHistory.length]);

  // Refresh scanner top picks when switching to scanner tab
  useEffect(() => {
    if (tab === "scanner" && owner) {
      topPicksAPI.getScanner(owner).then(r => setScannerTopPicks(r?.top_rated || []));
    }
  }, [tab, owner]);


  // 2026-06-17 — Per-broker EUR rate overrides. Each broker uses its own FX rate
  // (Millennium ~1.1736, Trade Republic ~1.165, etc.). Stored as JSON map in localStorage.
  // If a broker has no override, we fall back to the live ECB rate.
  const [brokerFxOverrides, setBrokerFxOverrides] = useState(() => {
    try {
      const v = localStorage.getItem("mtrades_broker_fx_overrides");
      return v ? JSON.parse(v) : {};
    } catch { return {}; }
  });

  const updateBrokerFxOverride = useCallback((broker, val) => {
    setBrokerFxOverrides(prev => {
      const next = { ...prev };
      if (val === null || val === "" || isNaN(parseFloat(val))) {
        delete next[broker];
      } else {
        next[broker] = parseFloat(val);
      }
      try { localStorage.setItem("mtrades_broker_fx_overrides", JSON.stringify(next)); } catch (e) { /* noop */ }
      return next;
    });
  }, []);

  // Helper: normalize a position to its broker label (matches broker-breakdown logic)
  const getPosBroker = useCallback((pos) => {
    if (!pos) return "Autre";
    return pos.source === "trading212" ? "Trading 212" : (pos.broker || "Autre");
  }, []);

  // Convert position value to EUR using exchange rates.
  // If a broker override exists for this position's broker, use it instead of the live ECB rate.
  const toEUR = useCallback((amount, ticker, broker) => {
    const currCode = getTickerCurrencyCode(ticker);
    const localAmount = amount;
    if (currCode === "EUR") return localAmount;
    const rateToUSD = exchangeRates[currCode] || 1;
    const brokerOverride = broker && brokerFxOverrides[broker];
    const eurToUSD = (brokerOverride && brokerOverride > 0) ? brokerOverride : (exchangeRates["EUR"] || 1.17);
    return localAmount * rateToUSD / eurToUSD;
  }, [exchangeRates, brokerFxOverrides]);

  // Use DB portfolio only — no fallback to static data that shows wrong values
  const allPositions = useMemo(() => dbPortfolio, [dbPortfolio]);
  const ownerPositions = useMemo(() => allPositions.filter(p => p.owner === owner), [allPositions, owner]);
  const portfolio = useMemo(() => strategyFilter === "ALL" ? ownerPositions : ownerPositions.filter(p => (p.strategy || "LT") === strategyFilter), [ownerPositions, strategyFilter]);

  const { totalValue, totalCost, pnl, pnlPercent, helmiTotal, alTotal, dailyPnl, dailyPnlPct, ltCount, stCount, pricesReady } = useMemo(() => {
    // Prices are "ready" when at least 70% of positions have a live price OR postMarket/pre-market data.
    // This prevents infinite "..." if 1-2 obscure tickers (INFQ, WREE.L) never load prices.
    const totalPos = allPositions.length;
    const loadedPos = allPositions.filter(p => prices[p.ticker] !== undefined || postMarketData[p.ticker]?.pm || postMarketData[p.ticker]?.pre).length;
    const ready = totalPos === 0 || (loadedPos / totalPos) >= 0.7;
    // Fallback: if a ticker's price isn't loaded, use pos.avg (less accurate but better than 0)
    // This keeps totals reasonable during the brief loading window.
    // 2026-06-17 — Same broker-accurate logic (regular close only, no pre/post overlay).
    const safeGetPrice = (p) => {
      return prices[p.ticker] !== undefined ? prices[p.ticker] : p.avg;
    };
    const tv = ownerPositions.reduce((s, p) => s + toEUR(safeGetPrice(p) * p.qty, p.ticker, getPosBroker(p)), 0);
    const tc = ownerPositions.reduce((s, p) => s + toEUR(p.avg * p.qty, p.ticker, getPosBroker(p)), 0);
    const pl = tv - tc;
    const pp = tc > 0 ? (pl / tc) * 100 : 0;
    const ht = allPositions.filter(p => p.owner === "Helmi").reduce((s, p) => s + toEUR(safeGetPrice(p) * p.qty, p.ticker, getPosBroker(p)), 0);
    const at = allPositions.filter(p => p.owner === "Anne-Laure").reduce((s, p) => s + toEUR(safeGetPrice(p) * p.qty, p.ticker, getPosBroker(p)), 0);
    const dp = ownerPositions.reduce((s, p) => {
      const dc = dailyChanges[p.ticker];
      if (!dc) return s;
      const posValue = toEUR(safeGetPrice(p) * p.qty, p.ticker, getPosBroker(p));
      const dpPct = dc.dp || 0;
      const dayPnl = dpPct !== 0 ? posValue * dpPct / (100 + dpPct) : 0;
      return s + dayPnl;
    }, 0);
    const dpp = tv > 0 ? (dp / (tv - dp)) * 100 : 0;
    const lt = ownerPositions.filter(p => (p.strategy || "LT") === "LT").length;
    const st = ownerPositions.filter(p => (p.strategy || "LT") === "ST").length;
    return { totalValue: tv, totalCost: tc, pnl: pl, pnlPercent: pp, helmiTotal: ht, alTotal: at, dailyPnl: dp, dailyPnlPct: dpp, ltCount: lt, stCount: st, pricesReady: ready };
  }, [ownerPositions, allPositions, toEUR, dailyChanges, prices, postMarketData]);

  if (!user) return <AuthScreen onAuth={handleAuth} />;

  // No API key prompt
  const needsApiKey = !apiKey;

  const FULL_TABS = [
    { id: "portfolio", label: "Portfolio", icon: Wallet },
    { id: "portfolio-engine", label: "Portfolio Engine", icon: Layers },
    { id: "sniper", label: "Sniper Bot", icon: Crosshair },
    { id: "momentum", label: "Momentum", icon: Rocket },
    { id: "watchlist", label: "Watchlist", icon: Eye },
    { id: "earnings-radar", label: "Earnings Radar", icon: Zap },
    { id: "performance", label: "Performance", icon: Activity },
    { id: "flow", label: "Capital Flow", icon: Zap },
    { id: "scanner", label: "Scanner", icon: Radar },
    { id: "pullback", label: "Pullback Live", icon: TrendingDown },
    { id: "early-alpha", label: "Early Alpha", icon: Sparkles },
    { id: "innovation", label: "Innovation Lab", icon: FlaskConical },
    // HIDDEN — superseded by Sniper Bot
    // { id: "disciplined", label: "Bot Discipliné", icon: ShieldCheck },
    { id: "algo", label: "Algo Trading", icon: Cpu },
    { id: "copytrade", label: "Copy Trade", icon: Building2 },
    { id: "gems", label: "Pepites", icon: Gem },
    // HIDDEN — superseded by Sniper Bot
    // { id: "bot", label: "Bot T212", icon: Bot },
    { id: "ai", label: "AI Analyst", icon: Bot },
  ];
  // 💰 COST SAVING: Anne-Laure account is read-only (passive holdings). Hide the
  // LLM-heavy tabs (Algo, Bot, AI Analyst) and other features the user only uses
  // on his own account. Keeps essential views (portfolio, watchlist, perf, earnings).
  const TABS = owner === "Anne-Laure"
    ? FULL_TABS.filter(t => ["portfolio", "portfolio-engine", "watchlist", "earnings-radar", "performance"].includes(t.id))
    : FULL_TABS;

  const SECTOR_OPTIONS = [
    { key: "MEGA_CAP", label: "Mega Caps", icon: Building2 },
    { key: "MID_CAP", label: "Mid Caps", icon: Layers },
    { key: "SMALL_CAP", label: "Small Caps", icon: Scale },
    { key: "AI_SEMIS", label: "AI & Semis", icon: Cpu },
    { key: "DEFENSE", label: "Defense", icon: Shield },
    { key: "ENERGY", label: "Energy", icon: Fuel },
    { key: "CRYPTO_COINS", label: "Crypto", icon: Coins },
    { key: "CRYPTO", label: "Crypto Stocks", icon: Bitcoin },
    { key: "COMMODITIES", label: "Commodities", icon: Gem },
    { key: "GROWTH", label: "Growth", icon: TrendingUp },
    { key: "FINTECH", label: "Fintech", icon: Building2 },
    { key: "HEALTHCARE", label: "Healthcare", icon: Activity },
    { key: "BIOPHARMA", label: "BioPharma", icon: Droplets },
    { key: "NUCLEAR", label: "Nuclear", icon: Atom },
    { key: "SPACE", label: "Space", icon: Rocket },
    { key: "EUROPE_LUXE", label: "EU Luxe", icon: Star },
    { key: "EUROPE_TECH", label: "EU Tech", icon: Cpu },
    { key: "EUROPE_FINANCE", label: "EU Finance", icon: Building2 },
    { key: "CHINA_ASIA", label: "Chine & Asie", icon: Globe },
    { key: "ETFS", label: "ETFs", icon: BarChart3 },
  ];

  return (
    <div className="min-h-screen bg-slate-900 text-slate-100 flex flex-col">
      {/* HEADER */}
      <header className="bg-slate-800/80 backdrop-blur-xl border-b border-slate-700 px-3 sm:px-6 py-3 flex flex-wrap items-center gap-3 sm:gap-6 sticky top-0 z-50">
        <div className="flex items-center gap-3 pr-4 sm:pr-6 border-r border-slate-700 shrink-0 relative cursor-pointer" onClick={() => setTab("portfolio")} data-testid="logo-home">
          <div className="relative -my-3" style={{zIndex: 50}}>
            <img src="/mario_logo.png" alt="MTrades" className="w-14 h-14 sm:w-16 sm:h-16 object-contain hover:scale-110 transition-transform duration-300" style={{filter: "drop-shadow(0 4px 8px rgba(0,0,0,0.4))"}} />
          </div>
          <div>
            <div className="font-display text-base sm:text-lg font-bold tracking-tight">MTrades</div>
            <div className="text-[10px] text-slate-400 flex items-center gap-2">
              <span className={loading ? "status-sync" : apiKey ? "status-live" : ""} />
              {loading ? "SYNCING" : apiKey ? "LIVE" : "NO API KEY"}
            </div>
          </div>
        </div>
        <div className="flex flex-1 gap-3 sm:gap-4 overflow-x-auto py-1 min-w-0">
          {[{ label: "HELMI", value: formatEUR(helmiTotal), color: "text-cyan-400", needsQuotes: true },
            { label: "ANNE-LAURE", value: formatEUR(alTotal), color: "text-pink-400", needsQuotes: true },
            { label: "P&L", value: `${pnl >= 0 ? "+" : ""}${formatEUR(pnl)}`, color: pnl >= 0 ? "text-emerald-400" : "text-red-400", needsQuotes: true },
            { label: "VAR. JOUR", value: `${dailyPnl >= 0 ? "+" : ""}${formatEUR(dailyPnl)}`, color: dailyPnl >= 0 ? "text-emerald-400" : "text-red-400", needsQuotes: true }
          ].map((stat, i) => (
            <div key={i} className="flex-shrink-0">
              <div className="text-[10px] text-slate-400 font-medium">{stat.label}</div>
              {stat.needsQuotes && allPositions.length > 0 && Object.keys(prices).length === 0 && loading ? (
                <div className="flex items-center gap-1.5 mt-0.5">
                  <RefreshCw className="w-3 h-3 text-slate-400 animate-spin" />
                  <span className="text-[10px] text-slate-500 font-mono">...</span>
                </div>
              ) : (
                <div className={`font-mono text-sm sm:text-lg font-bold ${stat.color}`}>{stat.value}</div>
              )}
            </div>
          ))}
          {/* Market Status */}
          {Object.keys(marketStatus).length > 0 && (
            <div className="flex-shrink-0 border-l border-slate-600 pl-4 ml-2">
              <div className="text-[10px] text-slate-400 font-medium mb-1">MARKETS</div>
              <div className="flex gap-3">
                {Object.entries(marketStatus).map(([code, data]) => {
                  const statusLabel = data.status === "OPEN" ? "OPEN" : data.status === "PRE_MARKET" ? "PRE" : data.status === "AFTER_HOURS" ? "AFTER" : "";
                  const statusColor = data.status === "OPEN" ? "text-cyan-400" : data.status === "PRE_MARKET" ? "text-yellow-400" : data.status === "AFTER_HOURS" ? "text-orange-400" : "";
                  return (
                    <div key={code} className="flex items-center gap-1.5" title={`${data.name}: ${data.status}${data.info ? " - " + data.info : ""} (${data.local_time})`}>
                      <span className="text-base">{data.flag}</span>
                      <span className={`w-2 h-2 rounded-full ${
                        data.status === "OPEN" ? "bg-cyan-400 shadow-[0_0_6px_rgba(6,182,212,0.8)]" : 
                        data.status === "PRE_MARKET" ? "bg-yellow-400 animate-pulse" : 
                        data.status === "AFTER_HOURS" ? "bg-orange-400" :
                        "bg-zinc-600"
                      }`} />
                      {statusLabel && <span className={`text-[9px] font-bold ${statusColor}`}>{statusLabel}</span>}
                      <span className="text-[10px] text-slate-400 font-mono">{data.local_time}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {/* Price Alerts Bell */}
          <button onClick={() => setShowAlertPanel(!showAlertPanel)}
            className="relative p-2 sm:p-2.5 bg-slate-700 hover:bg-zinc-700 border border-slate-600 rounded-lg transition-colors" data-testid="price-alerts-bell" title="Alertes de prix">
            {triggeredAlerts.length > 0 ? <BellRing className="w-4 h-4 text-amber-400 animate-pulse" /> : <Bell className="w-4 h-4 text-slate-300" />}
            {(triggeredAlerts.length > 0 || priceAlerts.filter(a => a.status === "active").length > 0) && (
              <span className={`absolute -top-1 -right-1 w-4 h-4 rounded-full text-[8px] font-black flex items-center justify-center ${triggeredAlerts.length > 0 ? "bg-red-500 text-white animate-bounce" : "bg-slate-500 text-white"}`}>
                {triggeredAlerts.length > 0 ? triggeredAlerts.length : priceAlerts.filter(a => a.status === "active").length}
              </span>
            )}
          </button>
          <button onClick={() => { setSettingsKey(apiKey); setShowSettings(true); }}
            className="p-2 sm:p-2.5 bg-slate-700 hover:bg-zinc-700 border border-slate-600 rounded-lg transition-colors" data-testid="settings-btn" title="Settings">
            <Settings className="w-4 h-4 text-slate-300" />
          </button>
          <button onClick={() => fetchQuotes(apiKey)} disabled={loading} className="p-2 sm:p-2.5 bg-slate-700 hover:bg-zinc-700 border border-slate-600 rounded-lg transition-colors" data-testid="refresh-btn">
            <RefreshCw className={`w-4 h-4 text-slate-300 ${loading ? "animate-spin" : ""}`} />
          </button>
          <div className="hidden sm:flex items-center gap-2 pl-2 border-l border-slate-600">
            <div className="text-right">
              <div className="text-xs text-slate-200 font-medium">{user?.name}</div>
              <div className="text-[10px] text-slate-400">{user?.email}</div>
            </div>
            <button onClick={handleLogout} className="p-2.5 bg-slate-700 hover:bg-red-500/20 border border-slate-600 rounded-lg transition-colors" data-testid="logout-btn" title="Déconnexion">
              <LogOut className="w-4 h-4 text-slate-300" />
            </button>
          </div>
          <button onClick={handleLogout} className="sm:hidden p-2 bg-slate-700 hover:bg-red-500/20 border border-slate-600 rounded-lg transition-colors" data-testid="logout-btn-mobile" title="Déconnexion">
            <LogOut className="w-4 h-4 text-slate-300" />
          </button>
        </div>
      </header>

      {/* Price Alerts Panel */}
      {showAlertPanel && (
        <AlertsPanel
          owner={owner}
          onClose={() => setShowAlertPanel(false)}
          onAnalyze={(ticker) => { setAlgoTicker(ticker); setTab("algo"); runAlgoAnalysis(ticker); }}
          getTickerCurrency={getTickerCurrency}
        />
      )}

      {/* TICKER TAPE */}
      <div className="bg-slate-800/50 border-b border-slate-700/50 h-8 overflow-hidden hidden sm:flex items-center">
        <div className="flex animate-ticker whitespace-nowrap">
          {[...ownerPositions, ...ownerPositions].map((p, i) => {
            const pr = getPrice(p);
            const dc = dailyChanges[p.ticker];
            const dayPct = dc ? dc.dp : 0;
            return (
              <span key={i} className="px-5 text-xs font-mono border-r border-slate-700/50 flex items-center gap-3">
                <span className="text-slate-400 font-medium">{p.ticker}</span>
                <span className="text-slate-100">{getTickerCurrency(p.ticker)}{formatNumber(pr)}</span>
                <span className={`flex items-center gap-0.5 ${dayPct >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                  {dayPct >= 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                  {Math.abs(dayPct).toFixed(2)}%
                </span>
              </span>
            );
          })}
        </div>
      </div>

      {/* NAV */}
      <nav className="bg-slate-800/30 border-b border-slate-700">
        <div className="px-2 sm:px-6 flex items-center justify-between">
          <div className="flex overflow-x-auto">
            {TABS.map(t => (
              <button key={t.id} onClick={() => setTab(t.id)}
                className={`px-3 sm:px-6 py-3 text-xs sm:text-sm font-semibold flex items-center gap-1.5 sm:gap-2 border-b-2 transition-all relative whitespace-nowrap ${
                  tab === t.id ? "border-cyan-500 text-cyan-400 bg-cyan-500/5" : "border-transparent text-slate-400 hover:text-slate-200"
                }`} data-testid={`tab-${t.id}`}>
                <t.icon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />{t.label}
                {t.id === "watchlist" && watchlistAlertCount > 0 && tab !== "watchlist" && (
                  <span data-testid="watchlist-alert-badge" className="absolute -top-1 -right-1 w-5 h-5 bg-cyan-500 text-white text-[10px] font-black rounded-full flex items-center justify-center buy-zone-badge">
                    {watchlistAlertCount}
                  </span>
                )}
                {t.id === "flow" && flowAlertCount > 0 && tab !== "flow" && (
                  <span data-testid="flow-alert-badge" className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-[10px] font-black rounded-full flex items-center justify-center animate-pulse">
                    {flowAlertCount}
                  </span>
                )}
              </button>
            ))}
          </div>
          <div className="hidden sm:flex gap-2 bg-slate-700/50 p-1 rounded-lg shrink-0">
            {["Helmi", "Anne-Laure"].map(o => (
              <button key={o} onClick={() => { setOwner(o); setPortfolioOptimization(null); }}
                className={`px-4 py-1.5 text-sm font-semibold rounded-md transition-all ${owner === o ? (o === "Anne-Laure" ? "bg-gradient-to-r from-pink-400 to-rose-500 text-white shadow-glow" : "bg-gradient-to-r from-cyan-300 to-blue-500 text-slate-900 shadow-glow") : "text-slate-300 hover:text-white"}`}
                data-testid={`owner-${o.toLowerCase()}`}>{o}</button>
            ))}
          </div>
        </div>
        {/* Mobile owner toggle */}
        <div className="sm:hidden flex items-center justify-center gap-2 px-3 py-2 bg-slate-800/50 border-t border-slate-700/50">
          {["Helmi", "Anne-Laure"].map(o => (
            <button key={o} onClick={() => { setOwner(o); setPortfolioOptimization(null); }}
              className={`flex-1 px-3 py-1.5 text-xs font-bold rounded-lg transition-all text-center ${owner === o ? (o === "Anne-Laure" ? "bg-gradient-to-r from-pink-400 to-rose-500 text-white shadow-glow" : "bg-gradient-to-r from-cyan-300 to-blue-500 text-slate-900 shadow-glow") : "bg-slate-700/50 text-slate-400"}`}
              data-testid={`owner-mobile-${o.toLowerCase()}`}>{o}</button>
          ))}
        </div>
      </nav>

      {/* CONTENT */}
      <main className="flex-1 overflow-auto p-3 sm:p-6">

        {/* Economic Calendar Banner + Flash Info (single most important headline pinned first) */}
        {(economicEvents.length > 0 || flashNews.length > 0) && (
          <div className="mb-4 rounded-xl bg-gradient-to-r from-slate-800/80 to-slate-900/80 border border-slate-700/40 p-2.5 backdrop-blur-sm" data-testid="economic-calendar-banner">
            <div className="flex items-center gap-3 overflow-x-auto scrollbar-hide">

              {/* 🚨 FLASH INFO — one elegant pill with the top headline */}
              {flashNews.length > 0 && (() => {
                const top = flashNews[0];
                const colorMap = {
                  emerald: { bg: "bg-emerald-500/15", text: "text-emerald-300", border: "border-emerald-500/30", dot: "bg-emerald-400" },
                  rose: { bg: "bg-rose-500/15", text: "text-rose-300", border: "border-rose-500/30", dot: "bg-rose-400" },
                  amber: { bg: "bg-amber-500/15", text: "text-amber-300", border: "border-amber-500/30", dot: "bg-amber-400" },
                  cyan: { bg: "bg-cyan-500/15", text: "text-cyan-300", border: "border-cyan-500/30", dot: "bg-cyan-400" },
                  slate: { bg: "bg-slate-500/15", text: "text-slate-300", border: "border-slate-500/30", dot: "bg-slate-400" },
                };
                const c = colorMap[top.impact_color] || colorMap.slate;
                const cleanHeadline = (top.headline || "").replace(/ - [A-Za-z .]+$/, "");
                const truncated = cleanHeadline.length > 90 ? cleanHeadline.slice(0, 87) + "..." : cleanHeadline;
                return (
                  <a
                    href={top.url || "#"}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border shrink-0 cursor-pointer hover:brightness-125 transition-all ${c.bg} ${c.border} group`}
                    data-testid="flash-info-pill"
                    title={top.summary || top.headline}
                  >
                    <div className="flex items-center gap-1.5 shrink-0">
                      <span className={`w-1.5 h-1.5 rounded-full ${c.dot} animate-pulse`}></span>
                      <span className={`text-[10px] font-black uppercase tracking-wider ${c.text}`}>Flash</span>
                    </div>
                    <span className="text-slate-600">·</span>
                    <span className="text-[10px] font-bold text-slate-100 group-hover:text-white max-w-[340px] truncate">
                      {truncated}
                    </span>
                    <span className="text-[8px] text-slate-500 font-mono shrink-0">{top.datetime_str?.split(" ")[1] || ""}</span>
                  </a>
                );
              })()}

              {economicEvents.length > 0 && (
                <div className="flex items-center gap-1.5 shrink-0 pr-3 border-r border-slate-700/50">
                  <CalendarDays className="w-4 h-4 text-amber-400" />
                  <span className="text-[10px] font-black text-amber-400 uppercase tracking-wider">Eco</span>
                </div>
              )}
              {economicEvents.slice(0, 6).map((evt, i) => {
                const catColors = {
                  FED: "bg-red-500/15 text-red-300 border-red-500/30",
                  CPI: "bg-amber-500/15 text-amber-300 border-amber-500/30",
                  NFP: "bg-blue-500/15 text-blue-300 border-blue-500/30",
                  GDP: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
                  PMI: "bg-purple-500/15 text-purple-300 border-purple-500/30",
                  EARNINGS: "bg-cyan-500/15 text-cyan-300 border-cyan-500/30",
                };
                const cls = catColors[evt.category] || "bg-slate-500/15 text-slate-300 border-slate-500/30";
                const isToday = evt.days_until === 0;
                const isTomorrow = evt.days_until === 1;
                const dateLabel = isToday ? "AUJOURD'HUI" : isTomorrow ? "DEMAIN" : `J-${evt.days_until}`;
                return (
                  <div key={i} 
                    className={`flex items-center gap-2 px-2.5 py-1.5 rounded-lg border shrink-0 cursor-pointer hover:brightness-125 transition-all ${cls} ${isToday ? "ring-1 ring-red-400/50 animate-pulse" : ""}`} 
                    onClick={() => handleEcoEventClick(evt)}
                    data-testid={`eco-event-${i}`}>
                    <div className="text-center">
                      <span className={`text-[8px] font-black block ${isToday ? "text-red-300" : isTomorrow ? "text-amber-300" : "text-slate-400"}`}>{dateLabel}</span>
                      <span className="text-[7px] text-slate-500">{evt.date?.slice(5)}</span>
                    </div>
                    <div>
                      <span className="text-[9px] font-bold block leading-tight">{evt.event}</span>
                      <span className="text-[7px] text-slate-500">{evt.detail}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Economic Event Analysis Modal */}
        {selectedEcoEvent && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={() => setSelectedEcoEvent(null)} data-testid="eco-event-modal">
            <div className="bg-slate-900 border border-slate-700/60 rounded-2xl p-5 max-w-lg w-full mx-4 max-h-[80vh] overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()}>
              {/* Header */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                    selectedEcoEvent.category === "FED" ? "bg-red-500/20" :
                    selectedEcoEvent.category === "CPI" ? "bg-amber-500/20" :
                    selectedEcoEvent.category === "NFP" ? "bg-blue-500/20" :
                    selectedEcoEvent.category === "GDP" ? "bg-emerald-500/20" :
                    "bg-purple-500/20"
                  }`}>
                    <CalendarDays className={`w-4 h-4 ${
                      selectedEcoEvent.category === "FED" ? "text-red-400" :
                      selectedEcoEvent.category === "CPI" ? "text-amber-400" :
                      selectedEcoEvent.category === "NFP" ? "text-blue-400" :
                      selectedEcoEvent.category === "GDP" ? "text-emerald-400" :
                      "text-purple-400"
                    }`} />
                  </div>
                  <div>
                    <h3 className="text-sm font-black text-white">{selectedEcoEvent.event}</h3>
                    <p className="text-[10px] text-slate-500">{selectedEcoEvent.date} — {selectedEcoEvent.detail}</p>
                  </div>
                </div>
                <button onClick={() => setSelectedEcoEvent(null)} className="text-slate-500 hover:text-white transition-colors" data-testid="eco-modal-close">
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Loading */}
              {ecoAnalysisLoading && (
                <div className="flex flex-col items-center py-8">
                  <RefreshCw className="w-6 h-6 text-cyan-400 animate-spin mb-2" />
                  <p className="text-xs text-slate-400">Analyse IA en cours...</p>
                </div>
              )}

              {/* Analysis Result */}
              {ecoAnalysis?.analysis && !ecoAnalysisLoading && (
                <div className="space-y-3">
                  {/* Status badge */}
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-0.5 rounded-full text-[9px] font-black border ${
                      selectedEcoEvent.days_until === 0 ? "bg-red-500/20 text-red-300 border-red-500/30" :
                      selectedEcoEvent.days_until <= 3 ? "bg-amber-500/20 text-amber-300 border-amber-500/30" :
                      "bg-cyan-500/20 text-cyan-300 border-cyan-500/30"
                    }`}>
                      {ecoAnalysis.analysis.statut || (selectedEcoEvent.days_until === 0 ? "AUJOURD'HUI" : `J-${selectedEcoEvent.days_until}`)}
                    </span>
                    {ecoAnalysis.analysis.risque && (
                      <span className={`px-2 py-0.5 rounded-full text-[9px] font-bold border ${
                        ecoAnalysis.analysis.risque?.includes("ELEVE") ? "bg-red-500/20 text-red-300 border-red-500/30" :
                        ecoAnalysis.analysis.risque?.includes("MOYEN") ? "bg-amber-500/20 text-amber-300 border-amber-500/30" :
                        "bg-emerald-500/20 text-emerald-300 border-emerald-500/30"
                      }`}>
                        Risque: {ecoAnalysis.analysis.risque}
                      </span>
                    )}
                  </div>

                  {/* Main content */}
                  {(ecoAnalysis.analysis.resume || ecoAnalysis.analysis.previsions) && (
                    <div className="bg-slate-800/50 rounded-lg p-3 border border-slate-700/30">
                      <h4 className="text-[10px] font-bold text-slate-400 uppercase mb-1">{ecoAnalysis.is_past ? "Resume" : "Previsions"}</h4>
                      <p className="text-xs text-slate-200 leading-relaxed">{ecoAnalysis.analysis.resume || ecoAnalysis.analysis.previsions}</p>
                    </div>
                  )}

                  {(ecoAnalysis.analysis.impact_marche || ecoAnalysis.analysis.impact_historique) && (
                    <div className="bg-slate-800/50 rounded-lg p-3 border border-slate-700/30">
                      <h4 className="text-[10px] font-bold text-slate-400 uppercase mb-1">{ecoAnalysis.is_past ? "Impact Marche" : "Impact Historique"}</h4>
                      <p className="text-xs text-slate-200 leading-relaxed">{ecoAnalysis.analysis.impact_marche || ecoAnalysis.analysis.impact_historique}</p>
                    </div>
                  )}

                  {/* Sectors */}
                  {(ecoAnalysis.analysis.secteurs_impactes || ecoAnalysis.analysis.secteurs_a_surveiller) && (
                    <div>
                      <h4 className="text-[10px] font-bold text-slate-400 uppercase mb-1.5">Secteurs a surveiller</h4>
                      <div className="flex flex-wrap gap-1.5">
                        {(ecoAnalysis.analysis.secteurs_impactes || ecoAnalysis.analysis.secteurs_a_surveiller || []).map((s, i) => (
                          <span key={i} className="px-2 py-0.5 rounded-full text-[9px] font-bold bg-slate-700/50 text-slate-300 border border-slate-600/30">{s}</span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Strategy / Action */}
                  {(ecoAnalysis.analysis.que_faire || ecoAnalysis.analysis.strategie) && (
                    <div className="bg-cyan-500/10 rounded-lg p-3 border border-cyan-500/20">
                      <h4 className="text-[10px] font-bold text-cyan-400 uppercase mb-1">{ecoAnalysis.is_past ? "Que faire ?" : "Strategie"}</h4>
                      <p className="text-xs text-cyan-200 leading-relaxed">{ecoAnalysis.analysis.que_faire || ecoAnalysis.analysis.strategie}</p>
                    </div>
                  )}

                  {/* Next event */}
                  {ecoAnalysis.analysis.prochain_evenement && (
                    <p className="text-[10px] text-slate-500 italic pt-1 border-t border-slate-700/30">Prochain : {ecoAnalysis.analysis.prochain_evenement}</p>
                  )}

                  {/* Market context */}
                  {ecoAnalysis.market_context && (
                    <p className="text-[9px] text-slate-600 pt-1">Marche: {ecoAnalysis.market_context}</p>
                  )}
                </div>
              )}

              {/* Error */}
              {ecoAnalysis?.error && !ecoAnalysisLoading && (
                <div className="text-center py-6">
                  <p className="text-xs text-red-400">Analyse indisponible: {ecoAnalysis.error}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* API Key Missing Banner — DISABLED: app now works fine without a user-provided Finnhub key
            (server-side env fallback covers /algo, /finnhub, briefing). Kept logic in case we want
            a softer "améliorer données" hint later. */}
        {false && needsApiKey && (
          <div className="gradient-gold rounded-xl p-5 mb-6 flex items-center justify-between animate-fade-in" data-testid="api-key-banner">
            <div className="flex items-center gap-3">
              <Key className="w-6 h-6 text-yellow-400" />
              <div>
                <div className="text-sm font-bold text-white">Clé Finnhub requise</div>
                <div className="text-xs text-slate-300">Ajoutez votre clé API Finnhub dans les paramètres pour accéder aux données de marché en temps réel.</div>
              </div>
            </div>
            <button onClick={() => { setSettingsKey(""); setShowSettings(true); }}
              className="px-4 py-2 bg-yellow-500/20 text-yellow-300 border border-yellow-500/30 rounded-lg text-sm font-semibold flex-shrink-0">
              Configurer
            </button>
          </div>
        )}
        
        {/* PORTFOLIO TAB */}
        {tab === "portfolio" && (
          <>
            {/* HIDDEN — superseded by Sniper Bot */}
            {/* <BriefingExecutor owner={owner} /> */}
            <PortfolioTab
            totalValue={totalValue} pnl={pnl} pnlPercent={pnlPercent}
            dailyPnl={dailyPnl} dailyPnlPct={dailyPnlPct}
            realizedPnl={realizedPnl}
            quotesLoading={loading} hasPrices={Object.keys(prices).length > 0} lastUpdate={lastUpdate}
            toEUR={toEUR}
            brokerFxOverrides={brokerFxOverrides} liveEurRate={exchangeRates["EUR"]} updateBrokerFxOverride={updateBrokerFxOverride}
            portfolio={portfolio} ownerPositions={ownerPositions}
            ltCount={ownerPositions.filter(p => (p.strategy || "LT") === "LT").length}
            stCount={ownerPositions.filter(p => p.strategy === "ST").length}
            owner={owner}
            strategyFilter={strategyFilter} setStrategyFilter={setStrategyFilter}
            t212Syncing={t212Syncing} syncTrading212={syncTrading212}
            openAddModal={() => { setModalMode("add"); setEditingPosition(null); setShowPositionModal(true); }}
            portfolioCmd={portfolioCmd} setPortfolioCmd={setPortfolioCmd}
            portfolioCmdLoading={portfolioCmdLoading} portfolioCmdResult={portfolioCmdResult}
            isListening={isListening} listenTarget={listenTarget} toggleVoice={toggleVoice}
            sortCol={sortCol} sortDir={sortDir} setSortCol={setSortCol} setSortDir={setSortDir}
            getPrice={getPrice} dailyChanges={dailyChanges} athData={athData}
            supportResistance={supportResistance} sparklines={sparklines}
            earningsData={earningsData} postMarketData={postMarketData}
            valuationData={valuationData} marketStatus={marketStatus}
            convictionScores={convictionScores}
            closePrices={prices}
            analyzePosition={(pos) => { setAlgoTicker(pos.ticker); setTab("algo"); runAlgoAnalysis(pos.ticker); }}
            handleDeletePosition={handleDeletePosition}
            setEditingPosition={setEditingPosition} setModalMode={setModalMode}
            setShowPositionModal={setShowPositionModal}
            onPortfolioCommand={handlePortfolioCommand}
            pullbackOpps={pullbackOpps}
            pullbackLoading={pullbackLoading}
            onAnalyzePullback={(ticker) => { setAlgoTicker(ticker); setTab("algo"); runAlgoAnalysis(ticker); }}
            onExpertAnalyzePosition={(pos) => {
              // 🎯 Nouvelle version : ouvre Smart Analysis Claude (3-prompts) pour ce ticker
              setSmartAnalyzerTicker(pos.ticker);
            }}
            onExpertAnalysis={() => {
              // 🎯 Nouvelle version : ouvre Smart Portfolio Analysis Claude (diagnostic global)
              setSmartPortfolioOpen(true);
            }}
            onFindOpportunity={() => setSmartFinderOpen(true)}
            watchlistAlerts={watchlistAlerts}
            watchlistAlertsSummary={watchlistAlertsSummary}
            watchlistAlertsLoading={watchlistAlertsLoading}
            onScanWatchlist={scanWatchlistOpportunities}
            onAnalyzeTicker={(ticker) => { setAlgoTicker(ticker); setTab("algo"); runAlgoAnalysis(ticker); }}
            onTabChange={(t) => setTab(t)}
            topPicks={topPicks}
            topPicksAI={topPicksAI}
            topPicksAILoading={topPicksAILoading}
            onAnalyzeTopPicksAI={analyzeTopPicksAI}
            swingPicks={swingPicks}
            marketTiming={marketTiming}
            marketTimingLoading={marketTimingLoading}
            onRefreshMarketTiming={refreshMarketTiming}
            sectorHeatmap={sectorHeatmap}
            sectorHeatmapLoading={sectorHeatmapLoading}
            onRefreshSectorHeatmap={refreshSectorHeatmap}
            sectorDrilldown={sectorDrilldown}
            sectorDrilldownLoading={sectorDrilldownLoading}
            selectedSector={selectedSector}
            onOpenSectorDrilldown={openSectorDrilldown}
            onCloseSectorDrilldown={closeSectorDrilldown}
            algoTopRated={dailyTopPicks}
            sellSignals={sellSignals}
            analystRatings={analystRatings}
            briefingCache={briefingCache}
            setBriefingCache={setBriefingCache}
          />
          </>
        )}

        {/* SNIPER BOT TAB */}
        {tab === "sniper" && (
          <SniperBot />
        )}

        {/* PORTFOLIO ENGINE TAB */}
        {tab === "portfolio-engine" && (
          <PortfolioEngine owner={owner} />
        )}

        {/* MOMENTUM TAB */}
        {tab === "momentum" && (
          <MomentumWatchlist onAnalyze={(ticker) => { setAlgoTicker(ticker); setTab("algo"); }} cachedData={momentumData} setCachedData={setMomentumData} />
        )}

        {/* WATCHLIST TAB */}
        {tab === "watchlist" && (
          <WatchlistTab
            owner={owner}
            watchlist={watchlist}
            livePrices={watchlistLivePrices}
            onAnalyze={(ticker) => { setAlgoTicker(ticker); setTab("algo"); runAlgoAnalysis(ticker); }}
            onRefreshPrices={refreshWatchlistPrices}
            onRemove={removeFromWatchlist}
            onAdd={(ticker) => addToWatchlist(ticker)}
            onReload={loadWatchlist}
          />
        )}

        {/* PERFORMANCE TAB */}
        {tab === "performance" && (
          <PerformanceTab
            portfolio={portfolio}
            prices={prices}
            getPrice={getPrice}
            owner={owner}
            onAnalyze={(t) => { setAlgoTicker(t); setTab("algo"); runAlgoAnalysis(t); }}
            cachedSnapshots={perfSnapshots}
            setCachedSnapshots={setPerfSnapshots}
          />
        )}

        {/* BRIEFING TAB */}
        {/* CAPITAL FLOW TAB */}
        {tab === "flow" && (
          <div className="space-y-4">
            <SmartScreener
              owner={owner}
              onTickerClick={(ticker) => { setAlgoTicker(ticker); setTab("algo"); runAlgoAnalysis(ticker); }}
              portfolioTickers={(portfolio || []).map((p) => p.ticker)}
            />
            <USGovernmentHoldings
              onTickerClick={(ticker) => { setAlgoTicker(ticker); setTab("algo"); runAlgoAnalysis(ticker); }}
              portfolioTickers={(portfolio || []).map((p) => p.ticker)}
            />
            <PoliticiansWatch
              onTickerClick={(ticker) => { setAlgoTicker(ticker); setTab("algo"); runAlgoAnalysis(ticker); }}
              portfolioTickers={(portfolio || []).map((p) => p.ticker)}
            />
            <LongTermIdeas
              onTickerClick={(ticker) => { setAlgoTicker(ticker); setTab("algo"); runAlgoAnalysis(ticker); }}
              portfolioTickers={(portfolio || []).map((p) => p.ticker)}
            />
            <InsiderSmartMoney
              onTickerClick={(ticker) => { setAlgoTicker(ticker); setTab("algo"); runAlgoAnalysis(ticker); }}
            />
            <CapitalFlowPanel
              finnhubKey={apiKey || ""}
              onTickerClick={(ticker) => { setAlgoTicker(ticker); setTab("algo"); runAlgoAnalysis(ticker); }}
              onAlertCount={setFlowAlertCount}
              cachedData={capitalFlowData}
              setCachedData={setCapitalFlowData}
            />
          </div>
        )}

        {/* SCANNER TAB */}
        {tab === "scanner" && (
          <ScannerTab
            scanMode={scanMode} setScanMode={setScanMode}
            massiveScan={massiveScan} onTriggerMassiveScan={triggerMassiveScan}
            massiveFilter={massiveFilter} setMassiveFilter={setMassiveFilter}
            massiveSignalFilter={massiveSignalFilter} setMassiveSignalFilter={setMassiveSignalFilter}
            alertHistory={alertHistory}
            scanning={scanning} runMarketScan={runMarketScan}
            scanResults={scanResults} scanProgress={scanProgress}
            selectedSectors={selectedSectors} setSelectedSectors={setSelectedSectors}
            SECTOR_OPTIONS={SECTOR_OPTIONS}
            scanChatInput={scanChatInput} setScanChatInput={setScanChatInput}
            onAnalyze={(ticker) => { setAlgoTicker(ticker); setTab("algo"); runAlgoAnalysis(ticker); }}
            onAskAI={askAI}
            onSwitchToAnalyst={() => setTab("analysts")}
            aiLoading={aiLoading}
            algoTopRated={scannerTopPicks}
          />
        )}

        {/* INTRADAY PULLBACK TAB */}
        {tab === "pullback" && (
          <IntradayPullback />
        )}

        {/* EARNINGS RADAR TAB */}
        {tab === "earnings-radar" && (
          <EarningsRadar owner={owner} />
        )}

        {/* EARLY ALPHA TAB */}
        {tab === "early-alpha" && (
          <EarlyAlpha
            onAnalyze={(t) => { setAlgoTicker(t); setTab("algo"); runAlgoAnalysis(t); }}
          />
        )}

        {/* INNOVATION LAB TAB */}
        {tab === "innovation" && (
          <InnovationLab
            onAnalyze={(t) => { setAlgoTicker(t); setTab("algo"); runAlgoAnalysis(t); }}
          />
        )}

        {/* DISCIPLINED BOT TAB */}
        {tab === "disciplined" && (
          <DisciplinedBot owner={owner} />
        )}

        {/* EARNINGS CALENDAR TAB — removed (superseded by Earnings Radar) */}

        {/* ALGO TRADING TAB */}
        {tab === "algo" && (
          <>
          <AlgoTab
            algoTicker={algoTicker} setAlgoTicker={setAlgoTicker}
            algoAnalysis={algoAnalysis} setAlgoAnalysis={setAlgoAnalysis}
            algoLoading={algoLoading} algoHistory={algoHistory}
            chartData={chartData} chartTimeframe={chartTimeframe} setChartTimeframe={setChartTimeframe}
            chartLoading={chartLoading}
            stockPerf={stockPerf} stockFundamentals={stockFundamentals}
            fundamentalsPro={fundamentalsPro}
            backtestResult={backtestResult} backtestStrategy={backtestStrategy}
            setBacktestStrategy={setBacktestStrategy}
            backtestConclusion={backtestConclusion} setBacktestConclusion={setBacktestConclusion}
            portfolioOptimization={portfolioOptimization}
            runAlgoAnalysis={runAlgoAnalysis} runBacktest={runBacktest}
            runOptimization={runOptimization}
            portfolioValue={totalValue}
            onGetBacktestConclusion={async () => {
              setBacktestConclusion("loading");
              try {
                const res = await algoAPI.getBacktestConclusion(algoTicker, backtestResult, backtestStrategy, stockFundamentals, algoAnalysis, stockPerf);
                setBacktestConclusion(res);
              } catch (e) { console.error("Backtest conclusion error:", e); setBacktestConclusion("Erreur lors de l'analyse. Réessayez."); }
            }}
            ownerPositions={ownerPositions} prices={prices} valuationData={valuationData}
            onSendToCommittee={() => {
              setTab("ai");
              setPendingDeepDiveTicker(algoAnalysis?.ticker || "");
            }}
            onSetPriceAlert={handleSetPriceAlert}
            topRated={algoTopRated}
          />
          {algoTicker && <div className="mt-4"><SmartAnalyzer ticker={algoTicker} /></div>}
          </>
        )}

        {/* COPY TRADE TAB */}
        {tab === "copytrade" && (
          <CopyTradeTab onAnalyzeTicker={(ticker) => { setAlgoTicker(ticker); setTab("algo"); runAlgoAnalysis(ticker); }} />
        )}

        {/* PEPITES TAB */}
        <div style={{ display: tab === "gems" ? "block" : "none" }}>
          <GemsTab
            onAnalyze={(ticker) => { setAlgoTicker(ticker); setTab("algo"); runAlgoAnalysis(ticker); }}
            owner={owner}
          />
        </div>

        {/* BOT T212 TAB */}
        {tab === "bot" && (
          <BotTab
            finnhubKey={apiKey || ""}
            onAnalyze={(ticker) => { setAlgoTicker(ticker); setTab("algo"); runAlgoAnalysis(ticker); }}
          />
        )}

        {/* AI EXPERT TAB - Smart Analyse Claude (3-prompts deep dive) */}
        {tab === "ai" && (
          <AnalystTab
            owner={owner}
            portfolio={portfolio}
            initialTicker={pendingDeepDiveTicker}
          />
        )}
      </main>

      {/* FOOTER */}
      <footer className="border-t border-slate-700 px-6 py-2.5 bg-slate-800/50 flex justify-between text-xs text-slate-500">
        <span className="font-medium">MTrades v4.0 • Algorithmic Trading Engine</span>
        <span className="flex items-center gap-1"><AlertTriangle className="w-3 h-3" />For informational purposes only</span>
        <span className="font-mono">{lastUpdate ? `Updated ${lastUpdate.toLocaleTimeString()}` : ""}</span>
      </footer>

      {/* Settings Modal */}
      {showSettings && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50" onClick={() => setShowSettings(false)}>
          <div className="bg-slate-800 border border-slate-600 rounded-2xl p-6 w-full max-w-lg animate-fade-in" onClick={e => e.stopPropagation()} data-testid="settings-modal">
            <div className="flex justify-between items-center mb-6">
              <h2 className="font-display text-xl font-bold flex items-center gap-2"><Settings className="w-5 h-5 text-emerald-400" />Settings</h2>
              <button onClick={() => setShowSettings(false)} className="p-2 hover:bg-slate-700 rounded-lg"><X className="w-5 h-5 text-slate-300" /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-xs text-slate-400 font-medium uppercase tracking-wider mb-2 block">Finnhub API Key <span className="text-slate-500 normal-case font-normal">(optionnel)</span></label>
                <div className="relative">
                  <Key className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input type="text" value={settingsKey} onChange={e => setSettingsKey(e.target.value)}
                    placeholder="Laisser vide pour utiliser la clé serveur" className="w-full pl-11 pr-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white font-mono text-sm"
                    data-testid="settings-finnhub-key" />
                </div>
                <p className="text-xs text-slate-400 mt-2">L'app fonctionne sans clé. Ajoutez la vôtre pour des limites de requêtes plus élevées — gratuit sur <a href="https://finnhub.io" target="_blank" rel="noreferrer" className="text-emerald-400 underline">finnhub.io</a></p>
              </div>
              <div className="bg-slate-700/50 rounded-lg p-4">
                <div className="text-xs text-slate-400 mb-1">Compte</div>
                <div className="text-sm text-white">{user?.name} ({user?.email})</div>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-slate-700">
              <button onClick={() => setShowSettings(false)} className="px-4 py-2 bg-slate-700 text-slate-300 border border-slate-600 rounded-lg font-semibold">Annuler</button>
              <button onClick={handleSaveSettings} className="px-6 py-2 bg-gradient-to-r from-cyan-300 to-blue-500 text-slate-900 rounded-lg font-semibold shadow-glow" data-testid="settings-save">Sauvegarder</button>
            </div>
          </div>
        </div>
      )}

      {/* Position Modal */}
      <PositionModal
        isOpen={showPositionModal}
        onClose={() => { setShowPositionModal(false); setEditingPosition(null); }}
        position={editingPosition}
        onSave={modalMode === "add" ? handleAddPosition : handleUpdatePosition}
        onDelete={handleDeletePosition}
        mode={modalMode}
        defaultOwner={owner}
        supportResistance={supportResistance}
        prices={prices}
      />

      {/* 🎯 Smart Analyzer (Claude per-position) modal */}
      {smartAnalyzerTicker && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-start justify-center overflow-y-auto p-4 md:p-8" onClick={() => setSmartAnalyzerTicker(null)} data-testid="smart-analyzer-overlay">
          <div className="w-full max-w-4xl my-auto" onClick={e => e.stopPropagation()}>
            <SmartAnalyzer ticker={smartAnalyzerTicker} onClose={() => setSmartAnalyzerTicker(null)} />
          </div>
        </div>
      )}

      {/* 🎯 Smart Portfolio Analyzer (Claude global) modal */}
      {smartPortfolioOpen && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-start justify-center overflow-y-auto p-4 md:p-8" onClick={() => setSmartPortfolioOpen(false)} data-testid="smart-portfolio-overlay">
          <div className="w-full max-w-4xl my-auto" onClick={e => e.stopPropagation()}>
            <SmartPortfolioAnalyzer portfolio={portfolio} owner={owner} onClose={() => setSmartPortfolioOpen(false)} />
          </div>
        </div>
      )}

      {/* 🔍 Smart Finder (Claude - trouve-moi une action) modal */}
      {smartFinderOpen && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-start justify-center overflow-y-auto p-4 md:p-8" onClick={() => setSmartFinderOpen(false)} data-testid="smart-finder-overlay">
          <div className="w-full max-w-3xl my-auto" onClick={e => e.stopPropagation()}>
            <SmartFinderAnalyzer portfolio={portfolio} owner={owner} onClose={() => setSmartFinderOpen(false)} />
          </div>
        </div>
      )}
    </div>
  );
}
